import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, X } from "lucide-react";
import { IconaExcel, IconaPdf } from "../IconeFile";
import SceltaFoglioModal from "../SceltaFoglioModal";
import SceltaIntestazioneModal from "../SceltaIntestazioneModal";
import SceltaCellaModal from "../SceltaCellaModal";
import SceltaColonnaModal from "../SceltaColonnaModal";
import LegendaFunzioniModal, { FUNZIONI_ANTEPRIMA_ORDINI } from "../LegendaFunzioniModal";
import EstrazioneModal from "../EstrazioneModal";
import AvvisoModal from "../AvvisoModal";
import LegendaSviluppiModal from "../LegendaSviluppiModal";
import AssegnaSviluppoModal from "../AssegnaSviluppoModal";
import ApriTicketModal from "../ApriTicketModal";
import CorreggiCodiciModal from "../CorreggiCodiciModal";
import SezionePasso from "../SezionePasso";
import { useFinestraRighe } from "../useFinestraRighe";
import { useApp } from "../AppContext";
import { useAuth } from "../AuthContext";
import {
  caricaFile,
  getCampiOrdini,
  getTemplatesOrdini,
  getTemplateOrdini,
  rilevaTemplateOrdini,
  suggerisciMappingOrdini,
  salvaTemplateOrdini,
  eliminaTemplateOrdini,
  applicaMappingOrdini,
  esportaAnteprimaOrdini,
  avviaImportazioneOrdini,
  getModelliPdf,
  caricaPdfGrezzo,
  applicaModelloPdf,
  rilevaModelloPdf,
  eliminaModelloPdf,
  getLegendaSviluppi,
  salvaMappatureSviluppi,
  salvaSviluppiAzienda,
  eliminaSviluppoAzienda,
  getAziende,
  getConversioneCodici,
  salvaMappatureConversione,
} from "../api";
import ConversioneCodiciModal from "../ConversioneCodiciModal";
import CampiMancantiModal from "../CampiMancantiModal";

const ETICHETTE_TIPO = {
  ignora: "(Ignora)",
  colonna: "Colonna sorgente",
  colonnaEditabile: "Colonna sorgente editabile (converti codici)",
  fisso: "Valore fisso o formula",
  cella: "Valore da una cella del file",
  manuale: "Manuale (scrivo in anteprima)",
};

// Solo per la VISUALIZZAZIONE: il campo resta "Taglia_NomeCalzata" ovunque
// nei dati/config/API (scrive sempre sulla stessa colonna di TB_ORDERSIMPORT)
// — qui si mostra solo un'etichetta diversa, perché "calzata" confonde i
// clienti che non fanno calzature.
function etichettaCampo(campo) {
  return campo === "Taglia_NomeCalzata" ? "Taglia_NomeSviluppo" : campo;
}

// Flag verde persistente per "modello/template applicato" (sezioni PDF e
// Template cliente più sotto): stesso stile per entrambe.
const STILE_FLAG_APPLICATO = {
  color: "var(--cf-success-text)",
  background: "var(--cf-success-bg)",
  border: "1px solid var(--cf-success-border)",
  borderRadius: "6px",
  padding: "0.5rem 0.75rem",
  fontWeight: 600,
};

// Alcuni file cliente hanno colonne "striminzite" che contengono più
// informazioni insieme in una sola stringa. Stessa logica duplicata in
// routes/ordini.js e src/pages/Mapping.jsx — se la cambi qui, cambiala anche
// lì.
function estraiValore(valoreOrigine, estrazione) {
  const testo = String(valoreOrigine ?? "");
  // Vedi lo stesso commento in routes/ordini.js: un "a capo" dentro al
  // valore (vedi routes/pdfEstrattore.js unisciPezziCella) serve SOLO a
  // poter isolare ogni riga fisica con "Estrai una parte" → A capo — in
  // ogni altro caso va appiattito, anche quando il pezzo scelto non è
  // esattamente sul confine dell'a capo, coerente con quello che poi
  // esporta il server.
  const separaSuACapo = estrazione && estrazione.modalita !== "posizione" && (estrazione.separatore || " ") === "\n";
  const base = separaSuACapo ? testo : testo.replace(/\n/g, " ").trim();
  if (!estrazione) return base;
  // "regex": per testate a lunghezza variabile (es. il titolo Dior, dove le parole
  // cambiano da PDF a PDF): prende il gruppo "gruppo" (default 1) del primo match;
  // se non combacia restituisce vuoto, mai un pezzo a caso.
  if (estrazione.modalita === "regex") {
    try {
      const m = new RegExp(estrazione.pattern || "").exec(testo);
      return m ? String(m[Number.isFinite(Number(estrazione.gruppo)) ? Number(estrazione.gruppo) : 1] ?? "").trim() : "";
    } catch (e) {
      return "";
    }
  }
  if (estrazione.modalita === "posizione") {
    const len = base.length;
    let da = Number(estrazione.da);
    let a = Number(estrazione.a);
    if (!Number.isFinite(da)) da = 0;
    if (!Number.isFinite(a)) a = len - 1;
    const s = da < 0 ? Math.max(0, len + da) : Math.min(da, len);
    const e = a < 0 ? len + a : Math.min(a, len - 1);
    if (e < s) return "";
    return base.slice(s, e + 1);
  }
  const separatore = estrazione.separatore || " ";
  const parti = separatore === ""
    ? [base]
    : base.split(separatore).map((p) => p.trim()).filter((p) => p !== "");
  if (parti.length === 0) return "";
  // "minParti" (opzionale): se il testo ha meno pezzi di questa soglia,
  // vuoto invece di indicizzare — vedi lo stesso commento in
  // routes/ordini.js per il "perché" (un pezzo opzionale in mezzo che a
  // volte manca del tutto, non solo più corto).
  const minParti = Number(estrazione.minParti);
  if (Number.isFinite(minParti) && parti.length < minParti) return "";
  let indice = Number(estrazione.indice);
  if (!Number.isFinite(indice)) indice = -1;
  const i = indice < 0 ? parti.length + indice : indice;
  // "indiceA" (opzionale): intervallo di pillole da "indice" a "indiceA"
  // comprese, riunite con lo stesso separatore (stessa logica duplicata lato
  // backend e in Mapping.jsx).
  const indiceA = estrazione.indiceA == null ? NaN : Number(estrazione.indiceA);
  if (Number.isFinite(indiceA)) {
    if (i < 0 || i >= parti.length) return "";
    const fine = indiceA < 0 ? parti.length + indiceA : indiceA;
    return parti.slice(i, Math.min(fine, parti.length - 1) + 1).join(separatore === "\n" ? " " : separatore).trim();
  }
  return parti[i] ?? "";
}

// Il nome taglia nel file cliente ("36.5") spesso non è scritto come nel
// gestionale: mezza taglia con la frazione unicode ("36½"), con una barra
// ("36/5"), o senza separatore ("365") — cambia da cliente a cliente. Solo
// per anteprima dal vivo qui in pagina: la conversione vera avviene lato
// server (routes/ordini.js convertiTaglia), stessa logica duplicata.
function escapeRegex(testo) {
  return testo.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// simboliPersonalizzati (mapping.__simboliMezzaTagliaPersonalizzati): simboli
// di mezza taglia propri di un cliente, registrati dalla pagina stessa
// quando ne compare uno mai visto (es. "+" per Augusta) — vedi il banner
// "simboli taglia non riconosciuti" più sotto. Trattati come la barra sola.
function convertiTaglia(nomeTaglia, modalita, simboliPersonalizzati = []) {
  if (!modalita || modalita === "nessuna") return nomeTaglia;
  const testo = String(nomeTaglia ?? "").trim();
  // "37/" — solo la barra, senza numero dopo (es. PDF Balmain/Falco): stesso
  // significato di ".5" più sotto, vedi routes/ordini.js convertiTaglia.
  const simboliSoli = ["/", ...simboliPersonalizzati.filter(Boolean)];
  const matchSimboloSolo = testo.match(new RegExp(`^(\\d+)(?:${simboliSoli.map(escapeRegex).join("|")})$`));
  if (matchSimboloSolo) {
    const [, intero] = matchSimboloSolo;
    if (modalita === "mezza") return `${intero}½`;
    if (modalita === "barra") return `${intero}/5`;
    if (modalita === "compatta") return `${intero}5`;
    return nomeTaglia;
  }
  const match = testo.match(/^(\d+)[.,](\d+)$/);
  if (!match) return nomeTaglia;
  const [, intero, decimale] = match;
  if (modalita === "mezza") return decimale === "5" ? `${intero}½` : testo;
  if (modalita === "barra") return `${intero}/${decimale}`;
  if (modalita === "compatta") return `${intero}${decimale}`;
  return nomeTaglia;
}

// Una colonna come "36+" non è né una taglia intera ("36") né una delle
// notazioni decimali già note (".5"/",5"): se il suffisso dopo le cifre non
// è uno dei simboli già registrati (barra sola compresa), va segnalata
// all'utente invece di restare silenziosamente non convertita in anteprima.
function suffissoTagliaNonRiconosciuto(nomeColonna, simboliPersonalizzati) {
  const testo = String(nomeColonna ?? "").trim();
  if (/^\d+$/.test(testo)) return null;
  if (/^\d+[.,]\d+$/.test(testo)) return null;
  const match = testo.match(/^\d+(\D+)$/);
  if (!match) return null;
  const suffisso = match[1];
  if (suffisso === "/" || simboliPersonalizzati.includes(suffisso)) return null;
  return suffisso;
}

const ETICHETTE_CONVERSIONE_TAGLIA = {
  nessuna: "Nessuna (come nel file)",
  mezza: "Frazione — 36½",
  barra: "Barra — 36/5",
  compatta: "Compatta — 365",
};

export default function OrdiniMapping() {
  const navigate = useNavigate();
  const { risultatoPdfOrdini, setRisultatoPdfOrdini } = useApp();
  // Chi non ha il permesso di mappare vede solo scegliere/applicare un
  // template già salvato + calcolare l'anteprima/avviare l'importazione: la
  // sezione "3. Mappa i campi" e la gestione dei template restano nascoste.
  // Anche qui solo l'interfaccia: gli endpoint di scrittura (salva/elimina
  // template, modelli PDF) restano protetti lato server in ogni caso.
  const { puoMappare, azienda, username } = useAuth();
  // wtrendy deve poter lavorare trasversalmente su ogni azienda: è lui (il
  // reparto tecnico) a preparare il PRIMO mapping di un cliente qualunque,
  // prima ancora che l'azienda finale lo usi — quindi qui non ha un'azienda
  // propria (aziendaDiUtente lato server torna null per lui) ma deve poter
  // scegliere PER QUALE azienda sta mappando in quel momento, e vedere/usare
  // i suoi sviluppi VERI (non una legenda a parte, altrimenti l'azienda
  // finale che riprende il template non troverebbe le stesse corrispondenze
  // già create). Un operatore normale invece non sceglie nulla: la sua
  // azienda è sempre e solo la propria.
  const eWtrendy = username === "wtrendy";
  const [aziendaWtrendySelezionata, setAziendaWtrendySelezionata] = useState("");
  const [aziendeDisponibili, setAziendeDisponibili] = useState([]);
  useEffect(() => {
    if (!eWtrendy) return;
    let annullato = false;
    getAziende().then((elenco) => { if (!annullato) setAziendeDisponibili(elenco); }).catch(() => {});
    return () => { annullato = true; };
  }, [eWtrendy]);
  const aziendaSviluppi = eWtrendy ? aziendaWtrendySelezionata : azienda;

  const [campiConfig, setCampiConfig] = useState(null);
  const [tabAttiva, setTabAttiva] = useState("testata");

  const [fileCaricato, setFileCaricato] = useState(null);
  // Byte originali dell'Excel caricato — servono SOLO per allegarlo a un
  // eventuale ticket di assistenza (vedi ApriTicketModal.jsx): /api/upload
  // con multer.memoryStorage() non li salva mai su disco (solo l'oggetto già
  // interpretato headers/righe finisce in data/uploads), quindi è l'unico
  // posto dove restano disponibili dopo la risposta del server.
  const [fileOriginaleGrezzo, setFileOriginaleGrezzo] = useState(null);
  const [mostraTicketPdf, setMostraTicketPdf] = useState(false);
  const [mostraTicketTemplate, setMostraTicketTemplate] = useState(false);
  // true dopo un invio riuscito: disabilita il pulsante "Apri ticket" per
  // questo stesso file, così non si rischia di aprirne un secondo per
  // errore riaprendo la modale. Azzerati insieme al resto ad ogni nuovo
  // file caricato — vedi processaFile.
  const [ticketPdfInviato, setTicketPdfInviato] = useState(false);
  const [ticketTemplateInviato, setTicketTemplateInviato] = useState(false);
  const [caricamento, setCaricamento] = useState(false);
  const [trascinamento, setTrascinamento] = useState(false);
  const inputRef = useRef(null);

  // Wizard "a fisarmonica" (solo per chi NON può mappare, vedi "wizard" più
  // sotto): quale passo già completato l'utente ha riaperto a mano per
  // rivederlo/cambiarlo — stessa idea di Home.jsx (Import Distinte Basi/DDT).
  const [stepRiaperto, setStepRiaperto] = useState(null);

  // Alcuni clienti mettono i dati veri non nel primo foglio di un Excel:
  // con più fogli il backend chiede di scegliere invece di indovinare il
  // primo (vedi routes/api.js /upload) — vedi anche Home.jsx, stessa idea.
  const [fileInAttesaFoglio, setFileInAttesaFoglio] = useState(null); // { file, fogli, nomeOriginale } | null
  const [fileInAttesaIntestazione, setFileInAttesaIntestazione] = useState(null); // { file, foglio, righeAnteprima, nomeOriginale } | null

  // Un PDF non ha colonne vere: serve un modello già calibrato per quel
  // layout (vedi CalibrazionePdf.jsx), stessa logica di Home.jsx per
  // diba/ddt — ma qui SEMPRE con modulo "ordini": stesso PDF di un cliente
  // letto in modo diverso a seconda del tracciato (colonne materiali vs
  // campi Testata/Riga/Taglie), quindi i modelli restano separati dalle
  // altre tab, mai condivisi.
  const [pdfCaricato, setPdfCaricato] = useState(null); // { pdfId, nomeOriginale, totalePagine, avviso? } | null
  const [modelliPdf, setModelliPdf] = useState([]);
  const [modelloPdfScelto, setModelloPdfScelto] = useState("");
  const [suggerimentoModello, setSuggerimentoModello] = useState(null);
  // Quale modello PDF risulta applicato al file attualmente caricato: a
  // differenza di pdfCaricato (che sparirebbe una volta applicato) questo
  // resta valorizzato per mostrare sempre il flag verde "modello applicato",
  // anche dopo che la sezione di scelta è stata usata.
  const [modelloPdfApplicato, setModelloPdfApplicato] = useState(null); // { cliente } | null
  const [modelliPdfSalvati, setModelliPdfSalvati] = useState([]);
  const [modelloPdfDaModificare, setModelloPdfDaModificare] = useState(null);
  const inputModificaModelloRef = useRef(null);

  // "Modifica" su un template Excel già salvato (vedi
  // chiediFilePerRidefinireTemplate più sotto): chiede subito un file e
  // forza la richiesta di foglio/riga intestazioni da capo, senza fidarsi
  // né del riconoscimento automatico né delle vecchie impostazioniFile
  // salvate nel template — sono proprio quelle che l'utente sta cercando
  // di correggere. Un ref (non uno state) perché va letto dentro
  // caricaFileExcel anche nelle chiamate "a catena" di procediConFoglio/
  // procediConIntestazione, dove uno state aggiornato nello stesso giro
  // non sarebbe ancora visibile.
  const forzaSceltaManualeRef = useRef(false);
  const inputRidefinisciFileRef = useRef(null);

  const [templates, setTemplates] = useState([]);
  const [templateSelezionato, setTemplateSelezionato] = useState("");
  const [templateAttivo, setTemplateAttivo] = useState(null);
  // true quando templateAttivo è "legato" al file attualmente caricato (lo
  // ha riconosciuto da solo il backend, o l'utente lo ha scelto/applicato
  // DOPO aver già caricato un file): in quel caso, se il file viene
  // rimosso, va rimosso anche il template, altrimenti un file successivo
  // (magari di un cliente diverso) erediterebbe le sue impostazioniFile
  // invece di essere ri-riconosciuto da solo. false quando l'utente ha
  // scelto/applicato il template PRIMA di caricare qualunque file (il caso
  // classico "so già chi è il cliente"): lì il template deve sopravvivere
  // alla rimozione del file, così ricaricarne un altro non richiede di
  // riselezionarlo.
  const [templateVincolatoAlFile, setTemplateVincolatoAlFile] = useState(false);
  const [suggerimentoTemplate, setSuggerimentoTemplate] = useState(null); // { id, cliente, punteggio } | null
  const [nomeCliente, setNomeCliente] = useState("");
  const [mapping, setMapping] = useState({});
  const [messaggioTemplate, setMessaggioTemplate] = useState("");

  // Legenda sviluppi (vedi LegendaSviluppiModal.jsx e routes/ordini.js): per
  // il campo Taglia_NomeCalzata, l'elenco degli sviluppi validi per l'ERP
  // della PROPRIA AZIENDA (non del cliente/brand del file — il vocabolario
  // lo cura wtrendy dal pannello Impostazioni, uguale per tutti i clienti di
  // questa azienda) + le corrispondenze "nome calzata trovato nel file ->
  // sviluppo giusto" già salvate in passato. Per wtrendy, aziendaSviluppi è
  // l'azienda scelta nel selettore qui sopra: finché non ne sceglie una,
  // resta vuota e gli strumenti sviluppi restano nascosti.
  const [legendaSviluppi, setLegendaSviluppi] = useState({ sviluppi: [], mappature: {} });
  const [mostraModaleSviluppi, setMostraModaleSviluppi] = useState(false);
  const [mostraModaleAssegna, setMostraModaleAssegna] = useState(false);

  useEffect(() => {
    if (!aziendaSviluppi) { setLegendaSviluppi({ sviluppi: [], mappature: {} }); return; }
    let annullato = false;
    getLegendaSviluppi(aziendaSviluppi)
      .then((dati) => { if (!annullato) setLegendaSviluppi(dati); })
      .catch(() => { if (!annullato) setLegendaSviluppi({ sviluppi: [], mappature: {} }); });
    return () => { annullato = true; };
  }, [aziendaSviluppi]);

  // Conversione codici (vedi ConversioneCodiciModal.jsx e routes/ordini.js):
  // a differenza della legenda sviluppi, questa è per TEMPLATE (non per
  // azienda) — le corrispondenze "codice fornitore trovato nel file -> mio
  // codice" hanno senso solo per QUEL fornitore/formato file, un fornitore
  // diverso potrebbe usare gli stessi codici per articoli diversi. Serve un
  // template già salvato (templateAttivo.id): finché non c'è, la mappatura
  // "colonna sorgente editabile" resta disponibile da scegliere ma senza
  // nessuna conversione da applicare/risolvere.
  const [conversioneCodici, setConversioneCodici] = useState({ mappature: {} });
  const [modaleConversione, setModaleConversione] = useState(null); // { campo } o null
  const [modaleCampoMancante, setModaleCampoMancante] = useState(null); // campo o null

  useEffect(() => {
    if (!templateAttivo?.id) { setConversioneCodici({ mappature: {} }); return; }
    let annullato = false;
    getConversioneCodici(templateAttivo.id)
      .then((dati) => { if (!annullato) setConversioneCodici(dati); })
      .catch(() => { if (!annullato) setConversioneCodici({ mappature: {} }); });
    return () => { annullato = true; };
  }, [templateAttivo?.id]);

  // SiglaImport non è più un campo scelto a mano (vedi la tabella di
  // mappatura, dove non compare più): appena il template attivo ha una
  // sigla — al caricamento di un template esistente o subito dopo averne
  // salvato uno nuovo — la si inietta come regola "fisso" nella mappatura,
  // così anteprima/export la usano automaticamente senza che l'operatore
  // debba fare nulla.
  useEffect(() => {
    if (!templateAttivo?.siglaImport) return;
    setMapping((m) => ({ ...m, SiglaImport: { tipo: "fisso", valore: templateAttivo.siglaImport } }));
  }, [templateAttivo?.siglaImport]);

  // Suggerimenti di mapping dallo storico dei template ordini (vedi
  // routes/ordini.js /ordini/suggerisci-mapping) — gemello di quello in
  // Mapping.jsx per Distinta Base/DDT. Si applicano SOLO ai campi ancora
  // liberi (ignora/manuale/senza valore), mai sopra una scelta già fatta.
  const [suggerimenti, setSuggerimenti] = useState({});
  const [suggerimentiNascosti, setSuggerimentiNascosti] = useState(false);
  const [suggerimentiIgnorati, setSuggerimentiIgnorati] = useState([]);
  const [modaleEstrazione, setModaleEstrazione] = useState(null); // { campo, nomeColonnaDestinazione, daZero? } | null
  const [modaleCella, setModaleCella] = useState(null); // { campo } | null
  const [modaleColonna, setModaleColonna] = useState(null); // { campo } | null
  const [mostraFunzioni, setMostraFunzioni] = useState(false);

  const [anteprimaRighe, setAnteprimaRighe] = useState([]);
  // Confronto "il file dichiara X, l'anteprima ne genera Y" per le colonne
  // taglia in orizzontale (vedi calcolaVerificaTotali in routes/ordini.js) —
  // null quando il mapping non usa taglie orizzontali, o prima del primo
  // calcolo anteprima. Blocca "Avvia importazione" se .ok è false: una
  // colonna taglia dimenticata in mappatura non fa sparire la riga
  // dall'anteprima (resta, solo con meno quantità), quindi da soli i dati in
  // tabella non bastano ad accorgersene.
  const [verificaTotali, setVerificaTotali] = useState(null);
  // Simboli di mezza taglia mai registrati, scoperti dal SERVER sui valori
  // REALI di Taglia_Nome dopo aver calcolato l'anteprima (vedi
  // /ordini/applica-mapping) — a differenza del banner nella tab Taglie
  // (solo sui nomi di colonna, formato orizzontale), questo copre anche il
  // formato verticale dove la taglia è il VALORE di una cella (es. template
  // AUGUSTA con Taglia_Nome sulla colonna "Taglia" del file): lì non c'è
  // modo di accorgersene prima di aver letto i dati.
  const [simboliTagliaAnteprima, setSimboliTagliaAnteprima] = useState([]);
  // Chiave stabile per riga (mai riusata, mai legata alla posizione
  // nell'array): serve solo per <tr key=...>, così eliminando una riga
  // React non confonde le righe sotto con quella tolta — a differenza
  // dell'indice, che dopo un'eliminazione slitta e farebbe mostrare valori
  // vecchi nelle celle non controllate (defaultValue, aggiornato solo al
  // montaggio) delle righe rimaste. refCellaAnteprima/scriviCellaAnteprima
  // restano invece a indice, perché operano sui DATI (anteprimaRighe),
  // ricalcolato correttamente a ogni render.
  const prossimaChiaveRiga = useRef(0);
  // Filtro di ricerca per colonna: { chiave: testo } — vedi la stessa
  // funzionalità in Revisione.jsx. Solo visuale, non tocca anteprimaRighe:
  // "↓ riempi"/AA/aa/import lavorano sempre su TUTTE le righe.
  const [filtriColonnaAnteprima, setFiltriColonnaAnteprima] = useState({});
  const [soloAnomalieCodice, setSoloAnomalieCodice] = useState(false);
  const [mostraCorreggiCodici, setMostraCorreggiCodici] = useState(false);
  const [calcolando, setCalcolando] = useState(false);
  const [avviando, setAvviando] = useState(false);
  const [messaggioImportazione, setMessaggioImportazione] = useState("");
  const [esportandoAnteprima, setEsportandoAnteprima] = useState(false);

  const [errore, setErrore] = useState("");

  useEffect(() => {
    getCampiOrdini().then(setCampiConfig).catch((e) => setErrore(e.message));
    ricaricaTemplates();
    ricaricaModelliPdfSalvati();
  }, []);

  // Esporta/Avvia importazione ora usano le righe già in tabella (comprese
  // le correzioni a mano), non un ricalcolo al volo da mapping+file (vedi
  // esportaAnteprima/avviaImportazione più sotto): se il mapping cambia dopo
  // aver calcolato l'anteprima, quella tabella non rispecchia più la
  // mappatura attuale e va invalidata, altrimenti si rischierebbe di
  // esportare/importare con una mappatura vecchia senza accorgersene.
  useEffect(() => {
    setAnteprimaRighe([]); setVerificaTotali(null); setSimboliTagliaAnteprima([]);
  }, [mapping]);

  // Suggerimenti dallo storico: si ricalcolano quando cambiano le colonne
  // del file caricato. Un suggerimento mancato non è un errore da mostrare.
  useEffect(() => {
    const headers = fileCaricato?.headers;
    if (!Array.isArray(headers) || headers.length === 0) { setSuggerimenti({}); return; }
    let annullato = false;
    suggerisciMappingOrdini(headers)
      .then(({ suggerimenti: s }) => { if (!annullato) setSuggerimenti(s || {}); })
      .catch(() => { if (!annullato) setSuggerimenti({}); });
    return () => { annullato = true; };
  }, [fileCaricato]);

  // Al ritorno dalla calibrazione PDF (nuovo modello o modifica di uno
  // esistente) il risultato arriva da questo canale dedicato del contesto,
  // non da stato locale: si "consuma" una volta sola e si azzera subito,
  // altrimenti resterebbe a metà se poi si arrivasse su questa pagina da un
  // altro percorso.
  useEffect(() => {
    if (!risultatoPdfOrdini) return;
    setFileCaricato(risultatoPdfOrdini);
    setAnteprimaRighe([]); setVerificaTotali(null); setSimboliTagliaAnteprima([]);
    setPdfCaricato(null);
    setSuggerimentoModello(null);
    setModelloPdfApplicato(risultatoPdfOrdini.clientePdf ? { cliente: risultatoPdfOrdini.clientePdf } : null);
    setRisultatoPdfOrdini(null);
  }, [risultatoPdfOrdini, setRisultatoPdfOrdini]);

  function ricaricaModelliPdfSalvati() {
    getModelliPdf("ordini").then(setModelliPdfSalvati).catch((e) => setErrore(e.message));
  }

  function ricaricaTemplates() {
    getTemplatesOrdini().then(setTemplates).catch((e) => setErrore(e.message));
  }

  // Tra i template salvati, quale corrisponde meglio alle colonne di questo
  // file — stessa idea del riconoscimento già fatto per Distinta Base/DDT
  // (vedi Home.jsx proponiTemplatePerHeaders) e per i modelli PDF.
  async function proponiTemplatePerHeaders(headers) {
    setSuggerimentoTemplate(null);
    try {
      const { migliore } = await rilevaTemplateOrdini(headers);
      if (migliore && migliore.punteggio >= 0.8) {
        setSuggerimentoTemplate(migliore);
        setTemplateSelezionato(migliore.id);
      }
    } catch (e) {
      // Riconoscimento best-effort: se fallisce si torna alla scelta manuale.
    }
  }

  // Se il template applicato era legato al file che sta per essere
  // sostituito (riconosciuto da solo, o scelto dalla tendina dopo che il
  // file era già caricato), va scordato insieme a lui: altrimenti il
  // prossimo file caricato (magari di un cliente diverso) erediterebbe le
  // sue impostazioniFile invece di essere ri-riconosciuto da solo — bug
  // segnalato dall'utente, prima "si sistemava" solo ricaricando la
  // pagina. Un template scelto PRIMA di caricare qualunque file resta
  // invece applicato: è la scelta esplicita di chi sa già di che cliente
  // si tratta. Chiamata sia da rimuoviFileCaricato che dall'inizio di
  // processaFile, perché un nuovo file può arrivare anche senza passare
  // prima da "Rimuovi" (drag&drop diretto sopra un file già caricato).
  // Ritorna true se ha azzerato (il chiamante non può fidarsi dello stato
  // React aggiornato nella stessa funzione: i setState sono asincroni,
  // quindi chi chiama usa il valore di ritorno, non ri-legge templateAttivo
  // subito dopo).
  function azzeraTemplateSeVincolatoAlFile() {
    if (!templateVincolatoAlFile) return false;
    setTemplateAttivo(null);
    setTemplateVincolatoAlFile(false);
    setMapping({});
    setNomeCliente("");
    setTemplateSelezionato("");
    setMessaggioTemplate("");
    return true;
  }

  async function processaFile(file) {
    if (!file) return;
    setErrore("");
    setAnteprimaRighe([]); setVerificaTotali(null); setSimboliTagliaAnteprima([]);
    setPdfCaricato(null);
    setSuggerimentoModello(null);
    setSuggerimentoTemplate(null);
    setMessaggioTemplate("");
    setModelloPdfApplicato(null);
    setTicketPdfInviato(false);
    setTicketTemplateInviato(false);
    const templateAppenaAzzerato = azzeraTemplateSeVincolatoAlFile();

    if (/\.pdf$/i.test(file.name)) {
      setCaricamento(true);
      try {
        const grezzo = await caricaPdfGrezzo(file);
        setPdfCaricato(grezzo);
        setFileCaricato(null);
        setFileOriginaleGrezzo(null);
        if (grezzo.avviso) {
          // PDF senza testo estraibile (probabile scansione): avviso subito,
          // niente da riconoscere/proporre.
          setErrore(grezzo.avviso);
          setModelliPdf([]);
          return;
        }
        const [modelli, rilevamento] = await Promise.all([
          getModelliPdf("ordini"),
          rilevaModelloPdf(grezzo.pdfId, "ordini").catch(() => ({ migliore: null })),
        ]);
        setModelliPdf(modelli);
        const trovato = rilevamento.migliore;
        if (trovato && trovato.punteggio >= 0.8 && modelli.some((m) => m.id === trovato.id)) {
          setSuggerimentoModello(trovato);
          setModelloPdfScelto(trovato.id);
        } else {
          setModelloPdfScelto(modelli[0]?.id || "");
        }
      } catch (err) {
        setErrore(err.message);
      } finally {
        setCaricamento(false);
      }
      return;
    }

    // Se un template è già applicato/selezionato, si riparte dalle sue
    // impostazioni salvate (foglio + riga/righe intestazione dell'ultimo
    // caricamento riuscito per questo cliente) invece che da zero: se il
    // file ha davvero sempre la stessa struttura, evita di richiedere di
    // nuovo SceltaFoglioModal/SceltaIntestazioneModal a ogni caricamento.
    // templateAppenaAzzerato: templateAttivo qui sopra potrebbe essere
    // appena stato azzerato in questa stessa chiamata (vedi
    // azzeraTemplateSeVincolatoAlFile) — lo stato React non è ancora
    // aggiornato a questo punto, quindi si usa il valore di ritorno invece
    // di rileggerlo, altrimenti si riapplicherebbero comunque le
    // impostazioniFile del template appena scartato.
    await caricaFileExcel(file, templateAppenaAzzerato ? {} : (templateAttivo?.impostazioniFile || {}));
  }

  async function caricaFileExcel(file, opzioni = {}) {
    setCaricamento(true);
    try {
      const risposta = await caricaFile(file, opzioni, "ordini", forzaSceltaManualeRef.current);
      if (risposta.serveSceltaFoglio) {
        setFileInAttesaFoglio({ file, fogli: risposta.fogli, nomeOriginale: risposta.nomeOriginale });
        return;
      }
      if (risposta.serveSceltaRigaIntestazioni) {
        setFileInAttesaIntestazione({
          file, foglio: opzioni.foglio, righeAnteprima: risposta.righeAnteprima, nomeOriginale: risposta.nomeOriginale,
        });
        return;
      }
      // Flusso "Modifica" completato (foglio/riga appena riconfermati a
      // mano): da qui in poi si torna al comportamento normale.
      forzaSceltaManualeRef.current = false;
      if (risposta.templateRiconosciuto) {
        // Caricamento "a freddo" (file trascinato senza scegliere prima un
        // template): il backend ha comunque riconosciuto da solo un template
        // salvato (stesso foglio/riga già noti, colonne combacianti — vedi
        // provaRiconoscimentoAutomatico in routes/api.js). Lo applichiamo
        // subito, stessa cosa che fa applicaTemplateEsistente da tendina.
        const t = await getTemplateOrdini(risposta.templateRiconosciuto.id);
        setTemplateAttivo(t);
        setTemplateVincolatoAlFile(true);
        setMapping(t.mapping || {});
        setNomeCliente(t.cliente || "");
        setTemplateSelezionato(t.id);
        setFileCaricato({ ...risposta, impostazioniFile: t.impostazioniFile });
        setFileOriginaleGrezzo(file);
        return;
      }
      // Foglio/riga REALMENTE usati (risposta.nomeFoglio/rigaIntestazioni...,
      // vedi routes/api.js): con un riconoscimento solo "strutturale" (nome
      // foglio combaciante ma colonne non più al 100%, es. una taglia
      // rinominata) il server sceglie foglio/riga da solo SENZA passare da
      // "opzioni" — usare "opzioni" qui (com'era prima) lascerebbe
      // fileCaricato.impostazioniFile vuoto in quel caso, e un successivo
      // "Aggiorna template" salverebbe il template SENZA foglio/riga,
      // perdendoli. Il fallback su "opzioni" resta per i PDF (dove risposta
      // non li valorizza) e per sicurezza.
      const impostazioniRisolte = {
        foglio: risposta.nomeFoglio ?? opzioni.foglio,
        rigaIntestazioni: Number.isInteger(risposta.rigaIntestazioni) ? risposta.rigaIntestazioni : opzioni.rigaIntestazioni,
        rigaIntestazioniSecondaria: risposta.rigaIntestazioniSecondaria ?? opzioni.rigaIntestazioniSecondaria ?? null,
      };
      setFileCaricato({ ...risposta, impostazioniFile: impostazioniRisolte });
      setFileOriginaleGrezzo(file);
      await proponiTemplatePerHeaders(risposta.headers);
    } catch (err) {
      forzaSceltaManualeRef.current = false;
      setErrore(err.message);
    } finally {
      setCaricamento(false);
    }
  }

  async function procediConFoglio(foglio) {
    if (!fileInAttesaFoglio) return;
    const { file } = fileInAttesaFoglio;
    setFileInAttesaFoglio(null);
    await caricaFileExcel(file, { foglio });
  }

  function annullaSceltaFoglio() {
    setFileInAttesaFoglio(null);
    forzaSceltaManualeRef.current = false;
  }

  async function procediConIntestazione(rigaIntestazioni, rigaIntestazioniSecondaria) {
    if (!fileInAttesaIntestazione) return;
    const { file, foglio } = fileInAttesaIntestazione;
    setFileInAttesaIntestazione(null);
    await caricaFileExcel(file, { foglio, rigaIntestazioni, rigaIntestazioniSecondaria });
  }

  function annullaSceltaIntestazione() {
    setFileInAttesaIntestazione(null);
    forzaSceltaManualeRef.current = false;
  }

  async function usaModelloPdfScelto() {
    if (!pdfCaricato || !modelloPdfScelto) return;
    setErrore("");
    setCaricamento(true);
    try {
      const risposta = await applicaModelloPdf(pdfCaricato.pdfId, modelloPdfScelto);
      setFileCaricato(risposta);
      setAnteprimaRighe([]); setVerificaTotali(null); setSimboliTagliaAnteprima([]);
      // pdfCaricato NON viene azzerato: la sezione di scelta del modello
      // resta sempre visibile (con il flag verde qui sotto), invece di
      // sparire non appena si applica un modello. suggerimentoModello NON si
      // azzera più qui (si azzerava, causando un bug: dopo un'applicazione
      // riuscita il messaggio "non riconosciuto" ricompariva subito perché
      // non c'era più nessun suggerimento a dimostrare il contrario) — resta
      // valorizzato e il banner "riconosciuto" si nasconde con
      // modelloPdfApplicato più sotto, come già per i template.
      const applicato = modelliPdf.find((m) => m.id === modelloPdfScelto);
      setModelloPdfApplicato(applicato ? { cliente: applicato.cliente } : null);
      await proponiTemplatePerHeaders(risposta.headers);
      if (risposta.avvisoLayout) setErrore(risposta.avvisoLayout);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setCaricamento(false);
    }
  }

  // Cambiare la tendina non deve mai "incastrare" chi non può mappare in un
  // vicolo cieco: prima si verificava SOLO il modello proposto in automatico
  // all'apertura del PDF — scegliere un altro (anche tornando poi su quello
  // giusto) restava "non riconosciuto" per sempre, senza modo di sbloccarsi
  // se non ricaricando tutto da capo. Qui invece si richiede al server un
  // punteggio fresco per QUALUNQUE modello scelto (rilevaModelloPdf ora
  // restituisce il punteggio di ogni candidato, non solo il migliore),
  // aggiornando "suggerimentoModello" di conseguenza: applicaModelloBloccato
  // si ricalcola sempre sulla scelta ATTUALE, mai su quella di un attimo fa.
  async function onCambiaModelloPdfScelto(nuovoId) {
    setModelloPdfScelto(nuovoId);
    if (!nuovoId || !pdfCaricato) { setSuggerimentoModello(null); return; }
    try {
      const { candidati } = await rilevaModelloPdf(pdfCaricato.pdfId, "ordini");
      setSuggerimentoModello((candidati || []).find((c) => c.id === nuovoId) || null);
    } catch (e) {
      setSuggerimentoModello(null);
    }
  }

  function calibraNuovoModelloPdf() {
    if (!pdfCaricato) return;
    navigate(`/ordini/calibrazione-pdf?pdfId=${pdfCaricato.pdfId}`);
  }

  function modificaModelloPdfScelto() {
    if (!pdfCaricato || !modelloPdfScelto) return;
    navigate(`/ordini/calibrazione-pdf?pdfId=${pdfCaricato.pdfId}&modelloId=${modelloPdfScelto}`);
  }

  function chiediFilePerModificaModello(modelloId) {
    setErrore("");
    setModelloPdfDaModificare(modelloId);
    inputModificaModelloRef.current?.click();
  }

  async function onFilePerModificaModello(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file || !modelloPdfDaModificare) return;
    setErrore("");
    setCaricamento(true);
    try {
      const grezzo = await caricaPdfGrezzo(file);
      navigate(`/ordini/calibrazione-pdf?pdfId=${grezzo.pdfId}&modelloId=${modelloPdfDaModificare}`);
    } catch (err) {
      setErrore(err.message);
      setCaricamento(false);
    }
  }

  async function eliminaModelloPdfSalvato(id) {
    if (!window.confirm("Eliminare questo modello PDF? I PDF futuri con questo layout non verranno più riconosciuti automaticamente.")) return;
    try {
      await eliminaModelloPdf(id);
      ricaricaModelliPdfSalvati();
    } catch (err) {
      setErrore(err.message);
    }
  }

  function onFileSelezionato(e) {
    processaFile(e.target.files[0]);
    e.target.value = "";
  }

  function onDrop(e) {
    e.preventDefault();
    setTrascinamento(false);
    if (caricamento) return;
    processaFile(e.dataTransfer.files?.[0]);
  }

  function rimuoviFileCaricato() {
    setFileCaricato(null);
    setFileOriginaleGrezzo(null);
    setAnteprimaRighe([]); setVerificaTotali(null); setSimboliTagliaAnteprima([]);
    setPdfCaricato(null);
    setSuggerimentoModello(null);
    setModelloPdfApplicato(null);
    azzeraTemplateSeVincolatoAlFile();
  }

  // Cambiare la tendina non deve mai "incastrare" chi non può mappare in un
  // vicolo cieco: prima si verificava SOLO il template proposto in automatico
  // all'apertura del file — scegliere un altro (anche tornando poi su quello
  // giusto) restava "non riconosciuto" per sempre, senza modo di sbloccarsi
  // se non ricaricando tutto da capo. Qui invece si richiede al server un
  // punteggio fresco per QUALUNQUE template scelto (rilevaTemplateOrdini ora
  // restituisce il punteggio di ogni candidato, non solo il migliore),
  // aggiornando "suggerimentoTemplate" di conseguenza: applicaTemplateBloccato
  // si ricalcola sempre sulla scelta ATTUALE, mai su quella di un attimo fa.
  async function onCambiaTemplateSelezionato(nuovoId) {
    setTemplateSelezionato(nuovoId);
    setMessaggioTemplate("");
    if (!nuovoId || !fileCaricato) { setSuggerimentoTemplate(null); return; }
    try {
      const { candidati } = await rilevaTemplateOrdini(fileCaricato.headers);
      setSuggerimentoTemplate((candidati || []).find((c) => c.id === nuovoId) || null);
    } catch (e) {
      setSuggerimentoTemplate(null);
    }
  }

  async function applicaTemplateEsistente(id) {
    if (!id) return;
    setErrore("");
    setMessaggioTemplate("");
    try {
      const t = await getTemplateOrdini(id);
      setTemplateAttivo(t);
      // Scelto PRIMA di un file (caso classico "so già chi è il cliente"):
      // sopravvive a una rimozione del file. Scelto DOPO che un file era
      // già caricato: è legato a quel file tanto quanto il riconoscimento
      // automatico, va rimosso insieme a lui — vedi rimuoviFileCaricato.
      setTemplateVincolatoAlFile(!!fileCaricato);
      setMapping(t.mapping || {});
      setNomeCliente(t.cliente || "");
      // Il flag verde qui sotto (legato a templateAttivo) mostra già "template
      // applicato": qui basta un messaggio transitorio solo per gli esiti di
      // salvataggio/aggiornamento (vedi salvaTemplate).
    } catch (err) {
      setErrore(err.message);
    }
  }

  // "Modifica" sulla tabella "Template salvati": a differenza di
  // applicaTemplateEsistente (pensata per un file già caricato, o per
  // scegliere a mano il cliente PRIMA di caricarlo) qui l'utente vuole
  // correggere un template che il file non rispetta più — tipicamente
  // foglio/riga intestazioni salvati ormai sbagliati (vedi impostazioniFile).
  // Chiede subito un file, carica la mappatura esistente SENZA cancellarla,
  // e forza la richiesta di foglio/riga da capo (forzaSceltaManualeRef) così
  // l'utente rivede le prime righe del file invece di ereditare in silenzio
  // le vecchie impostazioni o un riconoscimento automatico che le
  // ritroverebbe identiche.
  async function chiediFilePerRidefinireTemplate(id) {
    if (!id) return;
    setErrore("");
    setMessaggioTemplate("");
    try {
      const t = await getTemplateOrdini(id);
      setTemplateAttivo(t);
      setTemplateVincolatoAlFile(false);
      setMapping(t.mapping || {});
      setNomeCliente(t.cliente || "");
      setTemplateSelezionato(t.id);
      setFileCaricato(null);
      setFileOriginaleGrezzo(null);
      setPdfCaricato(null);
      setModelloPdfApplicato(null);
      setAnteprimaRighe([]); setVerificaTotali(null); setSimboliTagliaAnteprima([]);
      setSuggerimentoTemplate(null);
      forzaSceltaManualeRef.current = true;
      inputRidefinisciFileRef.current?.click();
    } catch (err) {
      setErrore(err.message);
    }
  }

  async function onFilePerRidefinireTemplate(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) {
      forzaSceltaManualeRef.current = false;
      return;
    }
    await caricaFileExcel(file, {});
  }

  // "Duplica": uno stesso formato file (es. lo stesso PO Balmain) a volte
  // serve a più aziende diverse (es. Maruska e Falco) — ma da quando un
  // template Ordini è assegnabile a UNA SOLA azienda (sigla import unica,
  // vedi PannelloImpostazioni.jsx), non si può più semplicemente assegnare
  // lo stesso template alla seconda: serve un secondo template, con la sua
  // sigla, ma senza dover rifare a mano tutta la mappatura colonne. Qui si
  // copia solo il mapping, SENZA portarsi dietro l'id del template
  // originale: templateAttivo resta null, quindi al salvataggio
  // salvaTemplate() lo tratta come un template NUOVO — nuovo id, nuova
  // sigla import generata da sola (routes/ordini.js), e (per wtrendy)
  // richiede comunque di scegliere l'azienda prima di salvare, esattamente
  // come per qualunque altro template nuovo — non eredita quella
  // dell'originale, va scelta apposta per evitare di salvarlo per sbaglio
  // sull'azienda sbagliata.
  async function duplicaTemplate(id) {
    setErrore("");
    setMessaggioTemplate("");
    try {
      const t = await getTemplateOrdini(id);
      setTemplateAttivo(null);
      setTemplateVincolatoAlFile(false);
      setMapping(t.mapping || {});
      setNomeCliente(`${t.cliente} (copia)`);
      setTemplateSelezionato("");
      setAziendaWtrendySelezionata("");
      setMessaggioTemplate(`Mapping copiato da "${t.cliente}". Scegli il nome cliente e l'azienda per questa copia, poi salva per generare una sigla import nuova.`);
    } catch (err) {
      setErrore(err.message);
    }
  }

  function nuovoTemplate() {
    setTemplateAttivo(null);
    setTemplateVincolatoAlFile(false);
    setMapping({});
    setNomeCliente("");
    setTemplateSelezionato("");
    setAnteprimaRighe([]); setVerificaTotali(null); setSimboliTagliaAnteprima([]);
    setMessaggioTemplate("");
  }

  // Sblocca la scelta del template senza ripartire completamente da zero
  // come "Nuovo template" (che serve invece a mappare a mano un cliente mai
  // visto): qui si mantiene la selezione nel menu a tendina, comoda se si
  // vuole solo scegliere un template diverso da quello applicato per errore.
  function cambiaTemplate() {
    setTemplateAttivo(null);
    setTemplateVincolatoAlFile(false);
    setMapping({});
    setNomeCliente("");
    setAnteprimaRighe([]); setVerificaTotali(null); setSimboliTagliaAnteprima([]);
    setMessaggioTemplate("");
  }

  async function salvaTemplate() {
    setErrore("");
    setMessaggioTemplate("");
    if (!nomeCliente.trim()) {
      setErrore("Inserisci il nome del cliente per salvare il template");
      return;
    }
    // Solo per un template NUOVO (non ancora salvato): da lì la sigla è
    // fissa per sempre, quindi l'azienda va scelta PRIMA — se wtrendy salva
    // senza sceglierla, sigla e assegnazione finiscono su "wtrendy" invece
    // che sull'azienda vera del cliente (vedi routes/ordini.js).
    if (eWtrendy && !templateAttivo?.id && !aziendaWtrendySelezionata) {
      setErrore("Scegli prima l'azienda per cui stai mappando questo template (qui sopra), poi salva.");
      return;
    }
    try {
      const eraAggiornamento = !!templateAttivo?.id;
      let t = await salvaTemplateOrdini({
        id: templateAttivo?.id, cliente: nomeCliente.trim(), mapping, azienda: aziendaWtrendySelezionata,
        impostazioniFile: fileCaricato?.impostazioniFile,
      });
      // Un template NUOVO ottiene la sigla import solo QUI, in risposta a
      // questo stesso salvataggio (routes/ordini.js) — ma il campo
      // Testata/SiglaImport dentro "mapping" è un campo come un altro, non
      // si aggiorna da solo: se questo mapping veniva da "Duplica" (o da un
      // salvataggio precedente) porta ancora incollata la sigla del
      // template di origine. Senza questo controllo, le righe calcolate
      // scriverebbero quella sigla sbagliata nel gestionale finché non si
      // tocca di nuovo il template — si ri-salva subito con quella giusta,
      // prima di mostrare "salvato".
      if (mapping.SiglaImport?.valore !== t.siglaImport) {
        const mappingCorretto = { ...mapping, SiglaImport: { tipo: "fisso", valore: t.siglaImport } };
        t = await salvaTemplateOrdini({
          id: t.id, cliente: nomeCliente.trim(), mapping: mappingCorretto, azienda: aziendaWtrendySelezionata,
          impostazioniFile: fileCaricato?.impostazioniFile,
        });
        setMapping(mappingCorretto);
      }
      setTemplateAttivo(t);
      setMessaggioTemplate(eraAggiornamento ? "✅ Aggiornamento effettuato." : "✅ Template salvato.");
      ricaricaTemplates();
    } catch (err) {
      setErrore(err.message);
    }
  }

  async function rimuoviTemplate(id) {
    if (!window.confirm("Eliminare questo template ordini?")) return;
    try {
      await eliminaTemplateOrdini(id);
      if (templateAttivo?.id === id) nuovoTemplate();
      ricaricaTemplates();
    } catch (err) {
      setErrore(err.message);
    }
  }

  function cambiaTipo(campo, tipo) {
    setMapping((m) => {
      const prev = m[campo] || {};
      return { ...m, [campo]: { tipo, colonna: prev.colonna, valore: prev.valore } };
    });
  }

  function impostaColonna(campo, colonna) {
    setMapping((m) => ({ ...m, [campo]: { ...m[campo], tipo: "colonna", colonna, estrazione: undefined } }));
  }

  function impostaValore(campo, valore) {
    setMapping((m) => ({ ...m, [campo]: { ...m[campo], tipo: "fisso", valore } }));
  }

  function impostaCella(campo, cella) {
    setMapping((m) => ({ ...m, [campo]: { ...m[campo], tipo: "cella", cella } }));
  }

  function impostaEstrazione(campo, estrazione) {
    setMapping((m) => ({ ...m, [campo]: { ...m[campo], estrazione } }));
  }

  // Un campo è "libero" (un suggerimento può riempirlo) quando non ha ancora
  // una regola vera: manca, o è "ignora"/"manuale", o è "colonna"/"fisso"
  // senza valore scelto.
  function campoLibero(campo) {
    const r = mapping[campo];
    if (!r || r.tipo === "ignora" || r.tipo === "manuale") return true;
    if (r.tipo === "colonna") return !r.colonna;
    if (r.tipo === "fisso") return !String(r.valore ?? "").trim();
    return false;
  }

  const suggerimentiAttivi = useMemo(() => {
    const out = {};
    for (const [campo, s] of Object.entries(suggerimenti)) {
      if (suggerimentiIgnorati.includes(campo)) continue;
      if (!campoLibero(campo)) continue;
      out[campo] = s;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggerimenti, suggerimentiIgnorati, mapping]);

  function regolaDaSuggerimento(s) {
    return s.tipo === "fisso"
      ? { tipo: "fisso", valore: s.valore }
      : { tipo: "colonna", colonna: s.colonna, estrazione: undefined };
  }

  function applicaSuggerimento(campo) {
    const s = suggerimenti[campo];
    if (s) setMapping((m) => ({ ...m, [campo]: { ...m[campo], ...regolaDaSuggerimento(s) } }));
  }

  function applicaTuttiSuggerimenti() {
    setMapping((m) => {
      const next = { ...m };
      for (const [campo, s] of Object.entries(suggerimentiAttivi)) {
        next[campo] = { ...(next[campo] || {}), ...regolaDaSuggerimento(s) };
      }
      return next;
    });
  }

  // Alcuni file cliente hanno le taglie su colonne separate (una colonna per
  // misura, es. "35", "36", "37"...) invece che una riga per misura come si
  // aspetta il tracciato: in questo caso Taglia_Nome/Taglia_Qta non si
  // mappano più come gli altri campi, ma si scelgono direttamente le colonne
  // del file che rappresentano una misura — per ogni riga sorgente verrà
  // generata una riga di output per ciascuna colonna scelta con quantità
  // valida, ripetendo tutti gli altri campi (vedi routes/ordini.js
  // calcolaRigheOrdine).
  function attivaTaglieOrizzontali(attivo) {
    setMapping((m) => ({ ...m, __taglieOrizzontali: { attivo, colonne: attivo ? (m.__taglieOrizzontali?.colonne || []) : [] } }));
  }

  function alternaColonnaTaglia(colonna) {
    setMapping((m) => {
      const attuali = m.__taglieOrizzontali?.colonne || [];
      const nuove = attuali.includes(colonna) ? attuali.filter((c) => c !== colonna) : [...attuali, colonna];
      return { ...m, __taglieOrizzontali: { attivo: true, colonne: nuove } };
    });
  }

  function impostaConversioneTaglia(modalita) {
    setMapping((m) => ({ ...m, __conversioneTaglia: modalita }));
  }

  // Registra un simbolo di mezza taglia mai visto prima (es. "+" per
  // Augusta, vedi suffissoTagliaNonRiconosciuto) come "tipologia possibile"
  // per QUESTO template: da qui in poi viene trattato come la barra sola,
  // sia in anteprima che nel controllo totali — persiste insieme al resto
  // del mapping quando si salva/aggiorna il template, così il cliente non
  // va rispiegato da capo al prossimo file con lo stesso simbolo.
  // "colonneTrovate" (solo formato orizzontale, taglie su colonne separate):
  // le colonne del file che hanno fatto scattare il banner (es. "35+",
  // "36+"...) — un solo click registra il simbolo E le aggiunge alla
  // selezione delle colonne taglia, invece di lasciare all'utente un
  // secondo giro manuale per spuntarle una per una. Omesso (formato
  // verticale, es. template AUGUSTA con Taglia_Nome su una colonna "Taglia"
  // del file: "35+" è un VALORE di cella, non c'è nessuna colonna da
  // selezionare) — in quel caso si registra solo il simbolo.
  function registraSimboloTaglia(simbolo, colonneTrovate = []) {
    setMapping((m) => {
      const attuali = m.__simboliMezzaTagliaPersonalizzati || [];
      const aggiornato = {
        ...m,
        __simboliMezzaTagliaPersonalizzati: attuali.includes(simbolo) ? attuali : [...attuali, simbolo],
      };
      if (colonneTrovate.length > 0) {
        const colonneAttuali = m.__taglieOrizzontali?.colonne || [];
        aggiornato.__taglieOrizzontali = { attivo: true, colonne: [...new Set([...colonneAttuali, ...colonneTrovate])] };
      }
      return aggiornato;
    });
  }

  function rimuoviSimboloTaglia(simbolo) {
    setMapping((m) => ({
      ...m,
      __simboliMezzaTagliaPersonalizzati: (m.__simboliMezzaTagliaPersonalizzati || []).filter((s) => s !== simbolo),
    }));
  }

  // Alcuni file cliente scrivono un valore (es. il negozio o il modello di un
  // gruppo di righe) solo sulla prima riga del gruppo, lasciando vuote quelle
  // sotto per quella colonna — tipico di celle unite nel foglio originale.
  // L'utente sceglie qui su quali colonne "riempire in avanti" (vedi
  // routes/ordini.js applicaForwardFill) l'ultimo valore non vuoto visto,
  // PRIMA che il mapping venga applicato riga per riga.
  function attivaForwardFill(attivo) {
    setMapping((m) => ({ ...m, __forwardFill: { attivo, colonne: attivo ? (m.__forwardFill?.colonne || []) : [] } }));
  }

  function alternaColonnaForwardFill(colonna) {
    setMapping((m) => {
      const attuali = m.__forwardFill?.colonne || [];
      const nuove = attuali.includes(colonna) ? attuali.filter((c) => c !== colonna) : [...attuali, colonna];
      return { ...m, __forwardFill: { attivo: true, colonne: nuove } };
    });
  }

  // Righe di subtotale/totale intercalate tra i dati (es. "Style Total",
  // "Grand Total") da escludere prima di applicare il mapping — vedi
  // routes/ordini.js filtraRigheEscluse.
  function impostaEscludiRighe(campo, valore) {
    setMapping((m) => ({ ...m, __escludiRighe: { ...m.__escludiRighe, [campo]: valore } }));
  }

  async function calcolaAnteprima() {
    if (!fileCaricato) {
      setErrore("Carica un file per calcolare l'anteprima");
      return;
    }
    setErrore("");
    setCalcolando(true);
    try {
      const { righe, verificaTotali: verifica, simboliTagliaNonRiconosciuti } = await applicaMappingOrdini({ fileId: fileCaricato.fileId, mapping });
      setVerificaTotali(verifica || null);
      setSimboliTagliaAnteprima(simboliTagliaNonRiconosciuti || []);
      // Per le colonne "sorgente editabile" (conversione codici), il valore
      // grezzo calcolato ORA (prima di qualunque conversione) va conservato
      // a parte: la cella verrà riscritta col mio codice quando la
      // conversione è nota, ma serve ancora sapere qual era il codice
      // fornitore originale per capire se manca ancora una corrispondenza —
      // vedi applicaConversioniNoteAlleCelle e valoriConversioneNonRisolti.
      const campiEditabili = Object.entries(mapping).filter(([, r]) => r?.tipo === "colonnaEditabile").map(([c]) => c);
      setAnteprimaRighe(righe.map((r) => {
        const grezzo = {};
        for (const campo of campiEditabili) grezzo[campo] = r[campo] ?? "";
        return { ...r, __chiaveRiga: prossimaChiaveRiga.current++, __grezzoColonnaEditabile: grezzo };
      }));
      setRigaInEliminazione(null);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setCalcolando(false);
    }
  }

  // Modifica una cella della tabella di anteprima (sezione 4): stessa idea di
  // modificaCella in Revisione.jsx per Distinta Base/DDT. Usata sia per
  // correggere un valore mappato sia per compilare a mano le colonne di tipo
  // "manuale" (che arrivano vuote dal calcolo). Esporta/Avvia importazione
  // usano poi queste righe così come sono in pagina, comprese le modifiche.
  function modificaCellaAnteprima(indiceRiga, chiave, valore) {
    setAnteprimaRighe((prev) => {
      const copia = prev.slice();
      copia[indiceRiga] = { ...copia[indiceRiga], [chiave]: valore };
      return copia;
    });
  }

  // "🗑️ Elimina riga": tolgono righe non importabili (es. Taglia_Qta a 0,
  // che l'API rifiuta) prima di avviare l'import — vedi
  // righeConCampoObbligatorioMancante più sotto per come vengono segnalate.
  // Non elimina al primo click: prima evidenzia la riga ed entra "in attesa
  // di conferma" (rigaInEliminazione), il click stesso diventa "conferma
  // eliminazione" — un secondo click (su un'altra riga, o "✕ annulla")
  // sposta/toglie l'attesa senza eliminare nulla. Evita l'eliminazione per
  // click sbagliato su un elenco con tante righe vicine.
  const [rigaInEliminazione, setRigaInEliminazione] = useState(null);

  function chiediEliminaRigaAnteprima(indiceRiga) {
    setRigaInEliminazione(indiceRiga);
  }

  function annullaEliminaRigaAnteprima() {
    setRigaInEliminazione(null);
  }

  function confermaEliminaRigaAnteprima(indiceRiga) {
    setAnteprimaRighe((prev) => prev.filter((_, i) => i !== indiceRiga));
    setRigaInEliminazione(null);
  }

  function eliminaRigheConCampoObbligatorioMancante() {
    if (righeConCampoObbligatorioMancante.size === 0) return;
    if (!window.confirm(`Eliminare ${righeConCampoObbligatorioMancante.size} riga/e con campi obbligatori mancanti o non validi (es. quantità a 0)? Non si può annullare.`)) return;
    setAnteprimaRighe((prev) => prev.filter((_, i) => !righeConCampoObbligatorioMancante.has(i)));
  }

  // Riferimenti diretti agli <input> della tabella di anteprima, indicizzati
  // "rigaIndice|nomeColonna": servono per scrivere in celle DIVERSE da quella
  // dove l'utente sta digitando (incolla multi-cella, riempi colonna qui
  // sotto). Le celle restano input non controllati (defaultValue + onBlur,
  // come in Revisione.jsx) per non ridisegnare tutta la tabella a ogni tasto
  // premuto: aggiornare solo lo stato non basterebbe a far vedere il nuovo
  // valore in una cella che non si sta modificando direttamente, quindi si
  // aggiorna anche il DOM a mano.
  const celleAnteprimaRef = useRef({});
  function refCellaAnteprima(indiceRiga, chiave) {
    return (el) => { celleAnteprimaRef.current[`${indiceRiga}|${chiave}`] = el; };
  }
  function scriviCellaAnteprima(indiceRiga, chiave, valore) {
    modificaCellaAnteprima(indiceRiga, chiave, valore);
    const el = celleAnteprimaRef.current[`${indiceRiga}|${chiave}`];
    if (el) el.value = valore;
  }

  // "✏️ Correggi codici" (CorreggiCodiciModal.jsx): applica in blocco una
  // correzione a TUTTE le righe che hanno un certo valore in quella colonna
  // — non serve sapere gli indici riga, basta il valore da cercare, così lo
  // stesso codice sbagliato ripetuto su tante taglie/righe si sistema in un
  // colpo solo invece che riga per riga.
  function applicaCorrezioniCodice(colonna, correzioni) {
    const mappa = new Map(correzioni.map((c) => [c.valoreOriginale, c.valoreNuovo]));
    anteprimaRighe.forEach((riga, i) => {
      const attuale = String(riga[colonna] ?? "");
      if (mappa.has(attuale)) scriviCellaAnteprima(i, colonna, mappa.get(attuale));
    });
  }

  // Incolla multi-cella "come Excel": copiando un blocco di celle (da Excel,
  // o da un'altra colonna qui) e incollandolo su una cella di questa tabella,
  // si distribuisce su più righe (a capo) e più colonne (tabulazione) a
  // partire da lì — invece di dover incollare cella per cella a mano. Su un
  // incolla di una sola cella si lascia fare al browser normalmente.
  function onIncollaCellaAnteprima(e, indiceRiga, indiceColonna) {
    const testo = e.clipboardData.getData("text");
    if (!/\r|\n|\t/.test(testo)) return;
    e.preventDefault();
    const righeTesto = testo.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
    // Excel aggiunge quasi sempre un a-capo finale copiando un blocco di
    // celle: senza toglierlo si "consumerebbe" una riga di tabella in più
    // scrivendoci una stringa vuota inutilmente.
    if (righeTesto.length > 1 && righeTesto[righeTesto.length - 1] === "") righeTesto.pop();
    righeTesto.forEach((rigaTesto, offsetRiga) => {
      const rigaDestinazione = indiceRiga + offsetRiga;
      if (rigaDestinazione >= anteprimaRighe.length) return;
      rigaTesto.split("\t").forEach((valore, offsetColonna) => {
        const colDestinazione = indiceColonna + offsetColonna;
        if (colDestinazione >= colonneAnteprima.length) return;
        scriviCellaAnteprima(rigaDestinazione, colonneAnteprima[colDestinazione], valore);
      });
    });
  }

  // Riempie le celle VUOTE di una colonna copiando in basso l'ultimo valore
  // trovato sopra di esse, fino alla prossima cella già compilata (che non
  // viene toccata né superata): si scrive un valore in una cella vuota, poi
  // si preme "riempi" per farlo scendere solo sul tratto vuoto sotto di
  // essa, senza mai sovrascrivere un dato già presente. Si può ripetere più
  // volte su tratti diversi della stessa colonna in sicurezza.
  function riempiColonnaAnteprima(chiave) {
    let ultimoValore = null;
    anteprimaRighe.forEach((riga, i) => {
      const valoreAttuale = riga[chiave] ?? "";
      if (valoreAttuale !== "") { ultimoValore = valoreAttuale; return; }
      if (ultimoValore !== null) scriviCellaAnteprima(i, chiave, ultimoValore);
    });
  }

  // MAIUSCOLO/minuscolo su tutta la colonna in un colpo solo — vedi la stessa
  // funzionalità in Revisione.jsx: molti file cliente arrivano con i dati
  // tutti minuscoli o tutti maiuscoli, da uniformare prima di avviare
  // l'importazione senza doverli riscrivere riga per riga.
  function trasformaColonnaAnteprima(chiave, tipo) {
    anteprimaRighe.forEach((riga, i) => {
      const valoreAttuale = String(riga[chiave] ?? "");
      const valoreNuovo = tipo === "maiuscolo" ? valoreAttuale.toUpperCase() : valoreAttuale.toLowerCase();
      if (valoreNuovo !== valoreAttuale) scriviCellaAnteprima(i, chiave, valoreNuovo);
    });
  }

  // Stessa normalizzazione usata lato server (routes/ordini.js): spazi
  // multipli ridotti a uno, maiuscole/minuscole ignorate, per riconoscere
  // come "lo stesso valore" varianti scritte diversamente da un file
  // all'altro dello stesso cliente.
  function normalizzaSviluppo(v) {
    return String(v || "").trim().replace(/\s+/g, " ").toLowerCase();
  }

  // Bottone "🔤 Sviluppi" sulla colonna Taglia_NomeCalzata: prima applica in
  // blocco le corrispondenze già note (silenzioso, come AA/aa qui sopra),
  // poi apre il popup per risolvere quelle rimaste senza corrispondenza — il
  // popup ricalcola da solo (a ogni render) quali restano, leggendo lo stato
  // aggiornato di anteprimaRighe/legendaSviluppi.
  // Riscrive nella tabella (scriviCellaAnteprima) i valori di Taglia_NomeCalzata
  // per cui esiste già una corrispondenza salvata, secondo la legenda PASSATA
  // (non necessariamente quella in stato: subito dopo un salvataggio lo stato
  // non si è ancora aggiornato, quindi si passa il risultato fresco tornato
  // dall'API). Usata sia all'apertura del popup sia subito dopo ogni
  // salvataggio da dentro di esso, altrimenti una corrispondenza appena
  // creata resterebbe "sapiuta" ma non applicata alle celle.
  function applicaMappatureNoteAlleCelle(legenda) {
    anteprimaRighe.forEach((riga, i) => {
      const attuale = String(riga.Taglia_NomeCalzata ?? "").trim();
      if (!attuale) return;
      const chiave = normalizzaSviluppo(attuale);
      const giaValido = legenda.sviluppi.some((s) => normalizzaSviluppo(s) === chiave);
      if (giaValido) return;
      const mappato = legenda.mappature[chiave];
      if (mappato && mappato !== attuale) scriviCellaAnteprima(i, "Taglia_NomeCalzata", mappato);
    });
  }

  function apriGestioneSviluppi() {
    applicaMappatureNoteAlleCelle(legendaSviluppi);
    setMostraModaleSviluppi(true);
  }

  async function salvaMappatureCliente(mappature) {
    const aggiornata = await salvaMappatureSviluppi(aziendaSviluppi, mappature);
    setLegendaSviluppi(aggiornata);
    applicaMappatureNoteAlleCelle(aggiornata);
  }

  // Solo per wtrendy (vedi puoCreareSviluppi passato al popup): stesse
  // azioni "da admin" disponibili in Impostazioni, qui per non dover uscire
  // dall'anteprima solo per provare/gestire il vocabolario.
  async function creaSviluppoCliente(nome) {
    const aggiornata = await salvaSviluppiAzienda(aziendaSviluppi, [nome]);
    setLegendaSviluppi(aggiornata);
  }

  async function rimuoviSviluppoCliente(nome) {
    const aggiornata = await eliminaSviluppoAzienda(aziendaSviluppi, nome);
    setLegendaSviluppi(aggiornata);
  }

  // "🔤 Assegna sviluppo alle taglie" (AssegnaSviluppoModal.jsx): per i file
  // cliente senza affatto un "nome calzata" sorgente, scrive direttamente lo
  // sviluppo scelto su Taglia_NomeCalzata per più righe in un colpo solo —
  // o tutte uguali, o una per Riga_Articolo. Scrivendo il campo, la colonna
  // compare da sola nell'anteprima anche se non era mai stata mappata (vedi
  // colonneAnteprima, che segue le chiavi effettivamente presenti nelle righe).
  function applicaSviluppoATutteLeTaglie(sviluppo) {
    anteprimaRighe.forEach((_, i) => scriviCellaAnteprima(i, "Taglia_NomeCalzata", sviluppo));
  }

  function applicaSviluppoPerArticolo(assegnazioni) {
    anteprimaRighe.forEach((riga, i) => {
      const chiave = String(riga.Riga_Articolo ?? `__riga_${i}`);
      if (assegnazioni[chiave]) scriviCellaAnteprima(i, "Taglia_NomeCalzata", assegnazioni[chiave]);
    });
  }

  // Stessa normalizzazione usata lato server (routes/ordini.js): spazi
  // multipli ridotti a uno, maiuscole/minuscole ignorate.
  function normalizzaCodice(v) {
    return String(v || "").trim().replace(/\s+/g, " ").toLowerCase();
  }

  // Campi mappati come "colonna sorgente editabile" — vedi ETICHETTE_TIPO e
  // ConversioneCodiciModal.jsx. Ricalcolato dal mapping corrente, non da
  // colonneAnteprima: un campo può essere così mappato anche prima di aver
  // ricalcolato l'anteprima.
  const campiConversioneCodici = useMemo(
    () => Object.entries(mapping).filter(([, r]) => r?.tipo === "colonnaEditabile").map(([c]) => c),
    [mapping]
  );

  // Riscrive nella tabella (scriviCellaAnteprima) i valori dei campi
  // "colonna sorgente editabile" per cui esiste già una corrispondenza
  // salvata — stessa idea di applicaMappatureNoteAlleCelle per gli sviluppi,
  // ma il valore grezzo di partenza è quello conservato in
  // riga.__grezzoColonnaEditabile (calcolato una volta sola al calcolo
  // dell'anteprima), non il valore attuale della cella: dopo una conversione
  // la cella contiene già il MIO codice, non più quello del fornitore, e non
  // avrebbe più senso da cui ripartire per un nuovo confronto.
  function applicaConversioniNoteAlleCelle(conversione) {
    anteprimaRighe.forEach((riga, i) => {
      for (const campo of campiConversioneCodici) {
        const grezzo = String(riga.__grezzoColonnaEditabile?.[campo] ?? "").trim();
        if (!grezzo) continue;
        const mappato = conversione.mappature?.[campo]?.[normalizzaCodice(grezzo)];
        if (mappato && mappato !== riga[campo]) scriviCellaAnteprima(i, campo, mappato);
      }
    });
  }

  // Stesso principio dell'effetto analogo per gli sviluppi: applica in
  // automatico le conversioni già note appena cambia l'anteprima (o la
  // conversione salvata), senza dover aprire il popup — il popup resta solo
  // per i codici davvero nuovi.
  useEffect(() => {
    if (anteprimaRighe.length === 0 || campiConversioneCodici.length === 0) return;
    applicaConversioniNoteAlleCelle(conversioneCodici);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anteprimaRighe, conversioneCodici, campiConversioneCodici]);

  // Valori grezzi (per campo) ancora senza corrispondenza salvata — blocca
  // "Avvia importazione" finché non risolti, stesso trattamento degli
  // sviluppi non validi.
  const valoriConversioneNonRisolti = useMemo(() => {
    const risultato = {}; // { campo: [valori grezzi distinti] }
    for (const campo of campiConversioneCodici) {
      const mappatureCampo = conversioneCodici.mappature?.[campo] || {};
      const distinti = new Set();
      for (const riga of anteprimaRighe) {
        const grezzo = String(riga.__grezzoColonnaEditabile?.[campo] ?? "").trim();
        if (grezzo && !mappatureCampo[normalizzaCodice(grezzo)]) distinti.add(grezzo);
      }
      if (distinti.size > 0) risultato[campo] = [...distinti];
    }
    return risultato;
  }, [anteprimaRighe, conversioneCodici, campiConversioneCodici]);

  const totaleConversioniNonRisolte = Object.values(valoriConversioneNonRisolti).reduce((s, v) => s + v.length, 0);

  function apriConversioneCodici(campo) {
    applicaConversioniNoteAlleCelle(conversioneCodici);
    setModaleConversione({ campo });
  }

  async function salvaMappatureConversioneCampo(campo, mappature) {
    const aggiornata = await salvaMappatureConversione(templateAttivo.id, campo, mappature);
    setConversioneCodici(aggiornata);
    applicaConversioniNoteAlleCelle(aggiornata);
  }

  async function esportaAnteprima() {
    if (anteprimaRighe.length === 0) {
      setErrore("Calcola l'anteprima prima di esportarla");
      return;
    }
    setErrore("");
    setEsportandoAnteprima(true);
    try {
      await esportaAnteprimaOrdini({ righe: anteprimaRighe });
    } catch (err) {
      setErrore(err.message);
    } finally {
      setEsportandoAnteprima(false);
    }
  }

  async function avviaImportazione() {
    if (anteprimaRighe.length === 0) {
      setErrore("Calcola l'anteprima prima di avviare l'importazione");
      return;
    }
    if (valoriNomeSviluppoNonValidi.length > 0) {
      setErrore(`${valoriNomeSviluppoNonValidi.length} valori di Taglia_NomeSviluppo non sono negli sviluppi validi: usa "🔤 Sviluppi" qui sotto per risolverli.`);
      return;
    }
    if (totaleConversioniNonRisolte > 0) {
      setErrore(`${totaleConversioniNonRisolte} codici del fornitore non hanno ancora una conversione salvata: usa "🔀 Conversione" qui sotto per associarli.`);
      return;
    }
    if (righeConCampoObbligatorioMancante.size > 0) {
      setErrore(`${righeConCampoObbligatorioMancante.size} riga/e hanno un campo obbligatorio mancante o non valido (es. quantità a 0): usa "🗑️ Elimina" qui sotto per toglierle, oppure correggile.`);
      return;
    }
    if (verificaTotali && !verificaTotali.ok) {
      setErrore("Il totale delle quantità in anteprima non corrisponde al file caricato: controlla il riquadro \"Controllo totali\" qui sotto prima di avviare l'importazione.");
      return;
    }
    if (simboliTagliaAnteprima.length > 0) {
      setErrore(`Trovati simboli di mezza taglia mai registrati (${simboliTagliaAnteprima.map((s) => `"${s.simbolo}"`).join(", ")}): registrali nel riquadro qui sotto prima di avviare l'importazione, altrimenti quelle taglie resterebbero scritte così come sono nel file.`);
      return;
    }
    setErrore("");
    setMessaggioImportazione("");
    // Scrive davvero sulle tabelle di frontiera dell'ERP: a differenza degli
    // altri pulsanti di questa pagina, non è un'anteprima e non si annulla da
    // sola — una conferma esplicita qui è l'ultima occasione per fermarsi
    // prima che i dati partano.
    if (!window.confirm(`Stai per importare davvero ${anteprimaRighe.length} riga/e nel gestionale (tabella di frontiera). Procedere?`)) {
      return;
    }
    setAvviando(true);
    try {
      const risultato = await avviaImportazioneOrdini({ righe: anteprimaRighe });
      setMessaggioImportazione(`✅ ${risultato.messaggio || "Importazione completata."} (${risultato.righeImportate} righe)`);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setAvviando(false);
    }
  }

  const colonneAnteprima = useMemo(() => {
    if (!campiConfig) return [];
    const tutti = campiConfig.gruppi.flatMap((g) => g.campi);
    const usati = new Set();
    anteprimaRighe.forEach((r) => Object.keys(r).forEach((k) => usati.add(k)));
    return tutti.filter((c) => usati.has(c));
  }, [campiConfig, anteprimaRighe]);

  // Righe con un campo obbligatorio (SiglaImport, Riga_Articolo, Taglia_Qta
  // — config/campi-ordini.json) vuoto o, per i campi numerici, a zero: es.
  // una taglia con quantità 0, che l'API del gestionale rifiuta bloccando
  // TUTTA l'importazione (vedi la stessa regola "vuoto" in
  // routes/ordini.js, POST /ordini/importa) senza dire quale riga sia il
  // problema. Qui si segnalano riga per riga, per poterle correggere o
  // togliere (🗑️) PRIMA di provare ad avviare l'import, invece di scoprirlo
  // dopo con un errore generico che non dice cosa cercare.
  const righeConCampoObbligatorioMancante = useMemo(() => {
    if (!campiConfig) return new Map();
    const campiNumerici = campiConfig.campiNumerici || [];
    const campiObbligatori = campiConfig.campiObbligatori || [];
    const risultato = new Map(); // indiceRiga -> [campi mancanti]
    anteprimaRighe.forEach((riga, i) => {
      const mancanti = campiObbligatori.filter((campo) => {
        const valore = riga[campo];
        return campiNumerici.includes(campo) ? !Number(valore) : !String(valore ?? "").trim();
      });
      if (mancanti.length > 0) risultato.set(i, mancanti);
    });
    return risultato;
  }, [anteprimaRighe, campiConfig]);

  // Stessa cosa, ribaltata per campo invece che per riga — per il pulsante
  // "⚠️ N mancanti" nell'intestazione di ogni colonna obbligatoria e per
  // CampiMancantiModal.jsx, che mostra tutte le righe mancanti di UN campo
  // insieme invece di farle scorrere una per una nella tabella.
  const indiciMancantiPerCampo = useMemo(() => {
    const mappa = new Map(); // campo -> [indiceRiga, ...]
    for (const [i, campiMancanti] of righeConCampoObbligatorioMancante) {
      for (const c of campiMancanti) {
        if (!mappa.has(c)) mappa.set(c, []);
        mappa.get(c).push(i);
      }
    }
    return mappa;
  }, [righeConCampoObbligatorioMancante]);

  // Valori distinti di Taglia_NomeCalzata (colonna "nome calzata" convertita)
  // che NON sono ancora uno sviluppo valido per questo cliente — bloccano
  // "Avvia importazione" finché non risolti (vedi avviaImportazione e il
  // bottone "🔤 Sviluppi" qui sotto).
  const valoriNomeSviluppoNonValidi = useMemo(() => {
    if (!aziendaSviluppi || !colonneAnteprima.includes("Taglia_NomeCalzata")) return [];
    const validi = new Set(legendaSviluppi.sviluppi.map(normalizzaSviluppo));
    const distinti = new Set();
    for (const r of anteprimaRighe) {
      const v = String(r.Taglia_NomeCalzata ?? "").trim();
      if (v && !validi.has(normalizzaSviluppo(v))) distinti.add(v);
    }
    return [...distinti];
  }, [anteprimaRighe, colonneAnteprima, legendaSviluppi, aziendaSviluppi]);

  // Un gruppo per ogni Riga_Articolo distinto presente in anteprima, con
  // quante taglie ha — per AssegnaSviluppoModal.jsx ("uno sviluppo diverso
  // per articolo"). Stessa chiave usata in applicaSviluppoPerArticolo.
  const gruppiPerArticolo = useMemo(() => {
    if (!colonneAnteprima.includes("Riga_Articolo")) return [];
    const mappa = new Map();
    anteprimaRighe.forEach((riga, i) => {
      const chiave = String(riga.Riga_Articolo ?? `__riga_${i}`);
      if (!mappa.has(chiave)) mappa.set(chiave, { chiave, etichetta: riga.Riga_Articolo || "(senza articolo)", numeroTaglie: 0 });
      mappa.get(chiave).numeroTaglie += 1;
    });
    return [...mappa.values()];
  }, [anteprimaRighe, colonneAnteprima]);

  // Segnala le colonne con maiuscole/minuscole non uniformi (es. una riga
  // "MILANO" e un'altra "milano" o "Milano") — vedi la stessa funzionalità
  // in Revisione.jsx. Un valore senza NESSUNA lettera (solo numeri/simboli)
  // non conta: il maiuscolo/minuscolo non ha senso per lui.
  const colonneCasoNonUniforme = useMemo(() => {
    function profiloMaiuscoleMinuscole(valore) {
      const testo = String(valore ?? "").trim();
      if (!/[a-zà-ÿ]/i.test(testo)) return null;
      const tuttoMaiuscolo = testo === testo.toUpperCase();
      const tuttoMinuscolo = testo === testo.toLowerCase();
      if (tuttoMaiuscolo && !tuttoMinuscolo) return "maiuscolo";
      if (tuttoMinuscolo && !tuttoMaiuscolo) return "minuscolo";
      return "misto";
    }
    const risultato = new Set();
    for (const c of colonneAnteprima) {
      const profiliTrovati = new Set();
      for (const riga of anteprimaRighe) {
        const p = profiloMaiuscoleMinuscole(riga[c]);
        if (p) profiliTrovati.add(p);
      }
      if (profiliTrovati.size > 1) risultato.add(c);
    }
    return risultato;
  }, [anteprimaRighe, colonneAnteprima]);

  // Segnala i "codici" numerici (es. 24,71814 — cifre con UN separatore
  // decimale) con un problema da correggere: lunghezza in cifre diversa dal
  // resto della colonna (capita quando Excel perde uno zero finale nella
  // parte decimale) OPPURE del testo in più dopo il codice (es. "99.718163
  // (cotton)" — un'annotazione che il file cliente porta dietro ma che
  // l'ERP non riconosce come parte del codice articolo). Scatta SOLO se
  // OGNI valore non vuoto della colonna INIZIA con questa forma (solo cifre
  // + un separatore, testo dopo ignorato ai fini del riconoscimento — ma
  // comunque segnalato come anomalia): su una colonna di testo libero
  // (nomi, descrizioni) lunghezze diverse sono normali e non vanno
  // segnalate — vedi la stessa funzionalità in Revisione.jsx. Esclude i
  // campi data (config/campi-ordini.json, campiData): una data scritta
  // come "30.06.09" combacia con la stessa forma "cifre.cifre" di un
  // codice articolo (l'anno dopo il secondo punto diventa "testo extra"),
  // quindi verrebbe segnalata come anomalia per ogni singola riga — un
  // falso positivo, non un vero codice da correggere.
  const colonneCodiceNonUniforme = useMemo(() => {
    function analizzaCodice(valore) {
      const testo = String(valore ?? "").trim();
      const m = testo.match(/^(\d+)[.,](\d+)/);
      if (!m) return null;
      return { cifre: m[1].length + m[2].length, extra: testo.slice(m[0].length).trim() };
    }
    const campiData = new Set(campiConfig?.campiData || []);
    const risultato = new Map();
    for (const c of colonneAnteprima) {
      if (campiData.has(c)) continue;
      const infoPerRiga = [];
      let tutteICodici = true;
      for (let i = 0; i < anteprimaRighe.length; i++) {
        const testo = String(anteprimaRighe[i][c] ?? "").trim();
        if (!testo) continue;
        const info = analizzaCodice(testo);
        if (info === null) { tutteICodici = false; break; }
        infoPerRiga.push({ i, ...info });
      }
      if (!tutteICodici || infoPerRiga.length < 2) continue;
      const conteggio = new Map();
      for (const { cifre } of infoPerRiga) conteggio.set(cifre, (conteggio.get(cifre) || 0) + 1);
      let cifreAttese = null, massimo = 0;
      for (const [cifre, conto] of conteggio) if (conto > massimo) { massimo = conto; cifreAttese = cifre; }
      const righe = new Map();
      for (const { i, cifre, extra } of infoPerRiga) {
        if (cifre !== cifreAttese || extra) righe.set(i, { cifreTrovate: cifre, testoExtra: extra });
      }
      if (righe.size > 0) risultato.set(c, { cifreAttese, righe });
    }
    return risultato;
  }, [anteprimaRighe, colonneAnteprima, campiConfig]);

  // Unione, su tutte le colonne, delle righe segnalate da 🔢⚠️ (colonne di
  // soli codici numerici con un numero di cifre diverso dal resto) — serve
  // per "mostra solo le righe da correggere" qui sotto: su un file da
  // migliaia di righe non si può chiedere di scorrerle tutte per trovare
  // le poche decine/centinaia sospette.
  const righeConAnomaliaCodice = useMemo(() => {
    const insieme = new Set();
    for (const { righe } of colonneCodiceNonUniforme.values()) {
      for (const i of righe.keys()) insieme.add(i);
    }
    return insieme;
  }, [colonneCodiceNonUniforme]);

  // Righe da mostrare dopo i filtri di ricerca per colonna (+ eventuale
  // "solo righe da correggere"), con l'indice ORIGINALE preservato — vedi
  // la stessa funzionalità/stesso motivo in Revisione.jsx
  // (refCellaAnteprima/modificaCellaAnteprima si aspettano l'indice vero
  // dentro anteprimaRighe).
  const righeAnteprimaFiltrate = useMemo(() => {
    return anteprimaRighe
      .map((riga, i) => ({ riga, i }))
      .filter(({ riga, i }) => {
        if (soloAnomalieCodice && righeConAnomaliaCodice.size > 0 && !righeConAnomaliaCodice.has(i)) return false;
        return colonneAnteprima.every((c) => {
          const filtro = (filtriColonnaAnteprima[c] || "").trim().toLowerCase();
          if (!filtro) return true;
          return String(riga[c] ?? "").toLowerCase().includes(filtro);
        });
      });
  }, [anteprimaRighe, colonneAnteprima, filtriColonnaAnteprima, soloAnomalieCodice, righeConAnomaliaCodice]);
  const filtroAnteprimaAttivo = Object.values(filtriColonnaAnteprima).some((v) => (v || "").trim() !== "") || soloAnomalieCodice;

  // Windowing (vedi useFinestraRighe.js): su un file da migliaia di righe,
  // renderizzare un <input> per ogni cella di ogni riga blocca il browser
  // (misurato: 150.000 celle, oltre 45s per digitare nel filtro). Sotto
  // soglia il comportamento resta identico a prima. contenitoreScrollRef è
  // lo stesso div con lo scroll verticale di sempre.
  const contenitoreAnteprimaRef = useRef(null);
  const finestraAnteprima = useFinestraRighe(contenitoreAnteprimaRef, righeAnteprimaFiltrate.length);
  // Larghezza colonna stabile durante lo scroll SOLO quando virtualizzato:
  // il <table> normalmente calcola la larghezza dal contenuto REALMENTE
  // renderizzato, e con solo una finestra di righe montate le colonne
  // "salterebbero" larghezza scorrendo. Stesso calcolo di larghezzeColonne
  // in Revisione.jsx, su TUTTE le righe (non solo quelle in finestra).
  const larghezzeColonneAnteprima = useMemo(() => {
    if (!finestraAnteprima.virtualizzata) return null;
    const larghezze = {};
    for (const c of colonneAnteprima) {
      let max = c.length;
      for (const riga of anteprimaRighe) {
        const lunghezza = String(riga[c] ?? "").length;
        if (lunghezza > max) max = lunghezza;
      }
      larghezze[c] = Math.min(90, Math.max(14, max + 2));
    }
    return larghezze;
  }, [finestraAnteprima.virtualizzata, colonneAnteprima, anteprimaRighe]);

  // Applica da sola le corrispondenze già note appena l'anteprima è pronta
  // (o cambia), senza aspettare che l'operatore clicchi "🔤 Sviluppi": chi
  // rimappa un file già visto in passato non deve rimettere mano su valori
  // già gestiti — il bottone/popup restano lì SOLO per i valori davvero
  // nuovi. Riscrivere una cella già corretta non fa nulla (scriviCellaAnteprima
  // confronta prima), quindi il giro in più che questo effetto fa su se
  // stesso (la scrittura cambia anteprimaRighe, che è nelle sue dipendenze)
  // si ferma da solo al secondo passaggio.
  useEffect(() => {
    if (anteprimaRighe.length === 0) return;
    applicaMappatureNoteAlleCelle(legendaSviluppi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anteprimaRighe, legendaSviluppi]);

  if (!campiConfig) return <div className="pagina"><p className="testo-secondario">Caricamento...</p></div>;

  // Chi non può mappare non può nemmeno correggere un modello/template
  // sbagliato: applicarne uno riconosciuto solo in parte (o scelto a mano,
  // alla cieca) rischierebbe di mappare male i dati senza che se ne accorga.
  // Per lui "Applica" resta bloccato finché il riconoscimento automatico non
  // è al 100% E la scelta in tendina coincide ancora con quel suggerimento
  // (cambiando manualmente la tendina, o quando è solo parziale/assente, il
  // suggerimento si azzera — vedi gli onChange qui sotto — e si ricade qui).
  const applicaModelloBloccato = !puoMappare && !(suggerimentoModello && suggerimentoModello.punteggio >= 1 && suggerimentoModello.id === modelloPdfScelto);
  const applicaTemplateBloccato = !puoMappare && !(suggerimentoTemplate && suggerimentoTemplate.punteggio >= 1 && suggerimentoTemplate.id === templateSelezionato);

  // Wizard guidato "a fisarmonica", SOLO per chi non può mappare — stessa
  // idea di Home.jsx (Import Distinte Basi/DDT): chi può mappare deve poter
  // saltare liberamente tra le sezioni (la "3. Mappa i campi" compresa, che
  // per lui è comunque nascosta), un percorso rigido lo intralcerebbe
  // soltanto. Per lui la pagina resta esattamente come è sempre stata.
  const wizard = !puoMappare;
  const passiWizard = wizard
    ? [
        {
          chiave: "file",
          titolo: "Carica il file ordini del cliente",
          completo: !!fileCaricato || !!(pdfCaricato && !pdfCaricato.avviso),
          riepilogo: fileCaricato
            ? `${fileCaricato.nomeOriginale} — ${fileCaricato.totaleRighe} righe`
            : (pdfCaricato ? pdfCaricato.nomeOriginale : ""),
        },
        ...(pdfCaricato && !pdfCaricato.avviso
          ? [{
              chiave: "modelloPdf",
              titolo: "Applica il modello PDF",
              completo: !!modelloPdfApplicato,
              riepilogo: modelloPdfApplicato ? `Modello applicato: ${modelloPdfApplicato.cliente}` : "",
            }]
          : []),
        {
          chiave: "template",
          titolo: "Template cliente",
          completo: !!templateAttivo,
          riepilogo: templateAttivo ? `Template applicato: ${templateAttivo.cliente}` : "",
        },
        {
          chiave: "anteprima",
          titolo: "Anteprima e importazione",
          completo: anteprimaRighe.length > 0,
          riepilogo: anteprimaRighe.length > 0 ? `${anteprimaRighe.length} righe generate` : "",
        },
      ]
    : [];
  // Il primo passo non ancora completato: tutto quello prima è già fatto (si
  // comprime), quello è l'unico aperto di default, tutto quello dopo resta
  // bloccato finché non ci si arriva. -1 = tutti completati.
  const indiceAttivoWizard = passiWizard.findIndex((p) => !p.completo);

  function statoDiPasso(chiave) {
    if (!wizard) return null;
    const i = passiWizard.findIndex((p) => p.chiave === chiave);
    if (i === -1) return null;
    const passo = passiWizard[i];
    const ultimo = i === passiWizard.length - 1;
    // L'ultimo passo ("Anteprima e importazione") resta aperto anche da
    // completato: contiene Esporta/Avvia importazione, comprimerlo
    // nasconderebbe l'unico modo di proseguire. Gli altri, da completati, si
    // comprimono di default.
    const espansoDiDefault = i === indiceAttivoWizard || (passo.completo && ultimo);
    return {
      bloccato: indiceAttivoWizard !== -1 && i > indiceAttivoWizard,
      completo: passo.completo,
      espanso: stepRiaperto === chiave || espansoDiDefault,
      riepilogo: passo.riepilogo,
      onToggle: () => setStepRiaperto((prev) => (prev === chiave ? null : chiave)),
    };
  }

  const gruppoAttivo = campiConfig.gruppi.find((g) => g.chiave === tabAttiva);
  const taglieOriz = mapping.__taglieOrizzontali;

  // Tra le colonne scelte come "taglie" (checkbox più sotto), quali hanno un
  // suffisso mai registrato per questo template (es. "+" in "36+") — vedi
  // suffissoTagliaNonRiconosciuto. Raggruppate per simbolo, con un paio di
  // colonne d'esempio, per il banner "registra questo simbolo" nella tab
  // Taglie: senza questo l'utente scoprirebbe il problema solo guardando il
  // controllo totali o i dati importati, non appena carica il file.
  const simboliPersonalizzatiAttuali = mapping.__simboliMezzaTagliaPersonalizzati || [];
  // Scansiona TUTTE le colonne del file, non solo quelle già selezionate
  // come taglia (taglieOriz.colonne): altrimenti l'utente scoprirebbe un
  // simbolo nuovo solo DOPO averlo già scelto a mano colonna per colonna,
  // vanificando lo scopo del banner (farglielo notare lui, non il contrario).
  const simboliTagliaNonRiconosciuti = (() => {
    if (!taglieOriz?.attivo || !fileCaricato) return [];
    const perSimbolo = new Map();
    for (const colonna of fileCaricato.headers || []) {
      const suffisso = suffissoTagliaNonRiconosciuto(colonna, simboliPersonalizzatiAttuali);
      if (!suffisso) continue;
      if (!perSimbolo.has(suffisso)) perSimbolo.set(suffisso, []);
      perSimbolo.get(suffisso).push(colonna);
    }
    return [...perSimbolo.entries()].map(([simbolo, colonne]) => ({ simbolo, colonne }));
  })();

  // Campi obbligatori per l'API (config/campi-ordini.json, "campiObbligatori")
  // senza nessuna regola di mapping compilata — stessa idea di
  // colonneObbligatorieNonCompilate in Mapping.jsx per la Distinta Base: solo
  // un avviso qui in fase di mapping, il blocco vero (che considera anche le
  // righe effettivamente vuote) è lato server in /ordini/importa.
  const campiObbligatoriNonCompilati = (campiConfig.campiObbligatori || []).filter((campo) => {
    // Con le taglie in colonne separate, Taglia_Nome e Taglia_Qta non
    // passano da un mapping normale (arrivano dall'unpivot di
    // mapping.__taglieOrizzontali.colonne — Taglia_Nome è il nome della
    // colonna stessa, sempre valorizzato per costruzione): qui
    // risulterebbero "non mappati" per errore.
    if (taglieOriz?.attivo && (campo === "Taglia_Qta" || campo === "Taglia_Nome")) return false;
    const regola = mapping[campo];
    if (!regola || regola.tipo === "ignora") return true;
    if (regola.tipo === "colonna") return !regola.colonna;
    if (regola.tipo === "fisso") return !String(regola.valore ?? "").trim();
    return false;
  });

  return (
    <div className="pagina">
      <SezionePasso numero={1} titolo="Carica il file ordini del cliente" stato={statoDiPasso("file")}>
        <div
          className={`zona-upload${trascinamento ? " zona-upload--attiva" : ""}${caricamento ? " zona-upload--disabilitata" : ""}`}
          onClick={() => !caricamento && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); if (!caricamento) setTrascinamento(true); }}
          onDragLeave={() => setTrascinamento(false)}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !caricamento) inputRef.current?.click(); }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.pdf"
            onChange={onFileSelezionato}
            disabled={caricamento}
            style={{ display: "none" }}
          />
          <UploadCloud size={30} strokeWidth={1.5} className="zona-upload-icona" />
          <div className="zona-upload-testo">
            <strong>Trascina qui il file, o scegline uno</strong>
            <div className="zona-upload-formati">
              <span className="zona-upload-formato" title="Excel (.xlsx, .xls)"><IconaExcel size={18} /> .xlsx/.xls</span>
              <span className="zona-upload-formato" title="PDF (ordine stampato)"><IconaPdf size={18} /> .pdf</span>
            </div>
          </div>
          <button
            type="button"
            className="btn-primario zona-upload-bottone"
            disabled={caricamento}
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
          >
            {caricamento ? "Caricamento..." : "Scegli file"}
          </button>
        </div>
        {fileCaricato && (
          <p className="info-file zona-upload-esito">
            {/\.pdf$/i.test(fileCaricato.nomeOriginale) ? <IconaPdf size={18} /> : <IconaExcel size={18} />}
            <strong>{fileCaricato.nomeOriginale}</strong> — {fileCaricato.totaleRighe} righe, {fileCaricato.headers.length} colonne
            <button type="button" className="btn-link-danger zona-upload-rimuovi" onClick={rimuoviFileCaricato}>
              <X size={14} strokeWidth={2.5} /> Rimuovi
            </button>
          </p>
        )}
        {fileCaricato?.colonneAmbigue?.length > 0 && (
          // Rete di sicurezza lato server per quando SceltaIntestazioneModal
          // non è comparso (foglio/riga già noti da un template salvato, o
          // riconoscimento automatico) — vedi i commenti in routes/api.js.
          // Stesso identico messaggio del modale, qui perché quel modale
          // stavolta non si è nemmeno visto.
          <div className="errore" style={{ marginTop: "0.5rem" }}>
            ⚠️ {fileCaricato.colonneAmbigue.length} colonna/e di questo file hanno un'intestazione ambigua su due
            righe (un'etichetta di gruppo e il nome specifico nella stessa colonna) — controlla che sia stato scelto
            il nome giusto, non quello generico:
            <ul style={{ margin: "0.4rem 0 0", paddingLeft: "1.2rem" }}>
              {fileCaricato.colonneAmbigue.slice(0, 8).map((c) => (
                <li key={c.colonna}>
                  Colonna {c.colonna}: "{c.principale}" / "{c.specifica}" → usata "{c.risolto}"
                </li>
              ))}
            </ul>
            {fileCaricato.colonneAmbigue.length > 8 && (
              <p style={{ margin: "0.4rem 0 0" }}>...e altre {fileCaricato.colonneAmbigue.length - 8} colonna/e.</p>
            )}
          </div>
        )}
        {fileCaricato?.intestazioniDuplicate?.length > 0 && (
          <p className="errore" style={{ marginTop: "0.5rem" }}>
            ⚠️ Questo file ha colonne con lo stesso nome dopo la lettura dell'intestazione:{" "}
            <strong>{fileCaricato.intestazioniDuplicate.join(", ")}</strong> — i dati della prima vengono sovrascritti
            da quelli dell'ultima con lo stesso nome.
          </p>
        )}
        {!fileCaricato && !pdfCaricato && puoMappare && (
          <p className="testo-secondario" style={{ marginTop: "0.75rem" }}>
            Puoi anche mappare/modificare un template senza caricare un file: le colonne sorgente si scrivono a mano
            invece di sceglierle da un elenco. Servirà un file solo per calcolare l'anteprima o avviare l'importazione.
          </p>
        )}
      </SezionePasso>

      <input ref={inputModificaModelloRef} type="file" accept=".pdf" onChange={onFilePerModificaModello} style={{ display: "none" }} />
      <input ref={inputRidefinisciFileRef} type="file" accept=".xlsx,.xls" onChange={onFilePerRidefinireTemplate} style={{ display: "none" }} />

      {fileInAttesaFoglio && (
        <SceltaFoglioModal
          nomeFile={fileInAttesaFoglio.nomeOriginale}
          fogli={fileInAttesaFoglio.fogli}
          onScegli={procediConFoglio}
          onAnnulla={annullaSceltaFoglio}
        />
      )}

      {fileInAttesaIntestazione && (
        <SceltaIntestazioneModal
          nomeFile={fileInAttesaIntestazione.nomeOriginale}
          righeAnteprima={fileInAttesaIntestazione.righeAnteprima}
          onScegli={procediConIntestazione}
          onAnnulla={annullaSceltaIntestazione}
        />
      )}

      {pdfCaricato && !pdfCaricato.avviso && (
        // "titolo" solo in modalità wizard: per chi può mappare questa
        // sezione non ha mai avuto un header numerato, resta così.
        <SezionePasso numero={2} titolo={wizard ? "Applica il modello PDF" : null} stato={statoDiPasso("modelloPdf")}>
          <p className="info-file zona-upload-esito" style={{ marginTop: 0 }}>
            <IconaPdf size={18} />
            <strong>{pdfCaricato.nomeOriginale}</strong>
            <button
              type="button"
              className="btn-link-danger zona-upload-rimuovi"
              onClick={() => { setPdfCaricato(null); setSuggerimentoModello(null); setModelloPdfApplicato(null); }}
              title="Rimuovi il file caricato"
            >
              <X size={14} strokeWidth={2.5} /> Rimuovi
            </button>
          </p>
          {modelloPdfApplicato && (
            <p className="testo-secondario" style={STILE_FLAG_APPLICATO}>
              ✅ Modello applicato: {modelloPdfApplicato.cliente}
            </p>
          )}
          {!caricamento && !modelloPdfApplicato && !puoMappare && applicaModelloBloccato && (
            // Chi non può mappare non può calibrare/modificare un modello: se il
            // layout non è riconosciuto al 100% (vedi applicaModelloBloccato più
            // sotto) va detto chiaramente cosa fare, invece di lasciarlo
            // applicare un modello che potrebbe non essere quello giusto.
            // !caricamento evita che compaia per un attimo mentre il
            // riconoscimento sul PDF appena caricato è ancora in corso.
            ticketPdfInviato ? (
              <p style={{ ...STILE_FLAG_APPLICATO, marginTop: 0, marginBottom: "0.75rem" }}>
                ✅ Ticket inviato — attendi la risposta dell'assistenza, o carica un nuovo file.
              </p>
            ) : (
            <div className="errore">
              <p style={{ marginTop: 0, marginBottom: "0.5rem" }}>
                Modello PDF non riconosciuto: non puoi procedere da solo con questo file. Premi "Apri ticket" per
                inviare una richiesta di assistenza a wTrendy, che calibrerà il layout per te.
              </p>
              <button type="button" className="btn-primario" onClick={() => setMostraTicketPdf(true)}>📩 Apri ticket</button>
            </div>
            )
          )}
          {mostraTicketPdf && (
            <ApriTicketModal
              onInviato={() => setTicketPdfInviato(true)}
              modulo="Import Ordini"
              tipoProblema="pdf"
              nomeFile={pdfCaricato?.nomeOriginale}
              pdfId={pdfCaricato?.pdfId}
              azienda={azienda}
              punteggio={suggerimentoModello?.punteggio ?? null}
              onChiudi={() => setMostraTicketPdf(false)}
            />
          )}
          {suggerimentoModello && !modelloPdfApplicato && (
            <p className="testo-secondario">
              📎 Layout riconosciuto come <strong>"{suggerimentoModello.cliente}"</strong> ({Math.round(suggerimentoModello.punteggio * 100)}%
              di corrispondenza) — già selezionato qui sotto, verifica ed applica, o scegline un altro.
            </p>
          )}
          <div className="scelta-mapping">
            {modelliPdf.length > 0 && (
              <div className="scelta-blocco">
                <label>Usa un modello PDF già calibrato</label>
                <div className="riga-controlli">
                  <select
                    value={modelloPdfScelto}
                    onChange={(e) => onCambiaModelloPdfScelto(e.target.value)}
                    disabled={!!modelloPdfApplicato}
                  >
                    {modelliPdf.map((m) => <option key={m.id} value={m.id}>{m.cliente}</option>)}
                  </select>
                  <button
                    className="btn-primario"
                    disabled={caricamento || !!modelloPdfApplicato || applicaModelloBloccato}
                    onClick={usaModelloPdfScelto}
                    title={
                      modelloPdfApplicato
                        ? "Modello già applicato: clicca \"Cambia modello\" per sceglierne un altro"
                        : applicaModelloBloccato
                          ? "Il layout non è riconosciuto al 100%: contatta l'assistenza per calibrare o correggere il modello"
                          : undefined
                    }
                  >
                    Applica modello
                  </button>
                  {modelloPdfApplicato && (
                    <button onClick={() => setModelloPdfApplicato(null)}>Cambia modello</button>
                  )}
                  {puoMappare && (
                    <button disabled={caricamento} onClick={modificaModelloPdfScelto}>
                      Modifica modello
                    </button>
                  )}
                </div>
              </div>
            )}
            {puoMappare && (
              <div className="scelta-blocco">
                <label>Oppure calibra un nuovo modello per questo PDF</label>
                <button disabled={caricamento} onClick={calibraNuovoModelloPdf}>Calibra nuovo modello</button>
              </div>
            )}
          </div>
        </SezionePasso>
      )}

      <SezionePasso
        numero={wizard ? passiWizard.findIndex((p) => p.chiave === "template") + 1 : 2}
        titolo="Template cliente"
        stato={statoDiPasso("template")}
      >
        {fileCaricato && !caricamento && !templateAttivo && !puoMappare && applicaTemplateBloccato && (
          // Stessa idea del blocco sui modelli PDF più sopra: chi non può
          // mappare non può correggere un template sbagliato, quindi non può
          // applicarne uno riconosciuto solo in parte o scelto a mano. Ma va
          // mostrato SOLO dopo aver provato a riconoscere un template su un
          // file vero (fileCaricato) e a riconoscimento concluso (!caricamento)
          // — non appena si apre la pagina, prima ancora di caricare un file,
          // "nessun template ancora scelto" non è la stessa cosa di "non
          // riconosciuto".
          ticketTemplateInviato ? (
            <p style={{ ...STILE_FLAG_APPLICATO, marginTop: 0, marginBottom: "0.75rem" }}>
              ✅ Ticket inviato — attendi la risposta dell'assistenza, o carica un nuovo file.
            </p>
          ) : (
          <div className="errore">
            <p style={{ marginTop: 0, marginBottom: "0.5rem" }}>
              Template non riconosciuto: non puoi procedere da solo con questo file. Premi "Apri ticket" per
              inviare una richiesta di assistenza a wTrendy, che mapperà il file per te.
            </p>
            <button type="button" className="btn-primario" onClick={() => setMostraTicketTemplate(true)}>📩 Apri ticket</button>
          </div>
          )
        )}
        {mostraTicketTemplate && (
          <ApriTicketModal
            onInviato={() => setTicketTemplateInviato(true)}
            modulo="Import Ordini"
            tipoProblema="template"
            nomeFile={fileCaricato?.nomeOriginale}
            fileGrezzo={fileOriginaleGrezzo}
            azienda={azienda}
            punteggio={suggerimentoTemplate?.punteggio ?? null}
            onChiudi={() => setMostraTicketTemplate(false)}
          />
        )}
        {suggerimentoTemplate && !templateAttivo && (
          suggerimentoTemplate.punteggio >= 1 ? (
            <p className="testo-secondario">
              📎 Colonne riconosciute come template <strong>"{suggerimentoTemplate.cliente}"</strong> (100% di
              corrispondenza) — già selezionato qui sotto, verifica ed applica, o scegline un altro.
            </p>
          ) : (
            // Sotto al 100% chi non può mappare è già bloccato del tutto (vedi
            // applicaTemplateBloccato/il blocco "Apri ticket" più sopra) — qui
            // invece è un admin (l'unico per cui questo ramo può comparire, dato
            // il blocco), che PUÒ comunque applicare il template: deve però
            // vedere subito, in modo che non si possa perdere, che qualcosa nel
            // file è diverso da come il template è stato calibrato — non solo
            // "quanto" (percentuale) ma anche "cosa" (quali colonne mancano),
            // così può controllare il mapping invece di scoprirlo dopo, con
            // dati già importati sbagliati.
            <p className="errore">
              ⚠️ Il template <strong>"{suggerimentoTemplate.cliente}"</strong> corrisponde solo al{" "}
              {Math.round(suggerimentoTemplate.punteggio * 100)}% delle colonne attese: questo file sembra diverso da
              come il template è stato calibrato. Colonne non trovate:{" "}
              {suggerimentoTemplate.colonneNonTrovate?.join(", ") || "—"}. Controlla/correggi il mapping prima di
              applicare, invece di procedere così.
            </p>
          )
        )}
        {templateAttivo && (
          <>
            <p className="testo-secondario" style={STILE_FLAG_APPLICATO}>
              ✅ Template applicato: {templateAttivo.cliente}
            </p>
            {suggerimentoTemplate && suggerimentoTemplate.id === templateAttivo.id && suggerimentoTemplate.punteggio < 1 && (
              // Lo stesso avviso di sopra, ma che RESTA visibile dopo aver
              // applicato il template (prima spariva subito, sostituito dal
              // solo "✅ Template applicato" — troppo facile perdere di vista
              // che la corrispondenza non era perfetta una volta passati oltre).
              <p className="errore">
                ⚠️ Corrispondenza imperfetta con questo file ({Math.round(suggerimentoTemplate.punteggio * 100)}%) —
                colonne non trovate: {suggerimentoTemplate.colonneNonTrovate?.join(", ") || "—"}. I dati in anteprima
                potrebbero essere incompleti: controlla attentamente prima di avviare l'import.
              </p>
            )}
          </>
        )}
        <div className="scelta-mapping">
          <div className="scelta-blocco">
            <label>Usa un template già salvato</label>
            <div className="riga-controlli">
              <select
                value={templateSelezionato}
                onChange={(e) => onCambiaTemplateSelezionato(e.target.value)}
                disabled={!!templateAttivo}
              >
                <option value="">-- seleziona cliente --</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.cliente}</option>
                ))}
              </select>
              <button
                className="btn-primario"
                disabled={!templateSelezionato || !!templateAttivo || applicaTemplateBloccato}
                onClick={() => applicaTemplateEsistente(templateSelezionato)}
                title={
                  templateAttivo
                    ? "Template già applicato: clicca \"Cambia template\" per sceglierne un altro"
                    : applicaTemplateBloccato
                      ? "Le colonne non sono riconosciute al 100%: contatta l'assistenza per creare o correggere il template"
                      : undefined
                }
              >
                Applica template
              </button>
              {templateAttivo && <button onClick={cambiaTemplate}>Cambia template</button>}
              {puoMappare && <button onClick={nuovoTemplate}>Nuovo template</button>}
            </div>
          </div>
          {puoMappare && (
            <div className="scelta-blocco">
              {eWtrendy && !templateAttivo?.id && (
                <label
                  style={{ display: "block", marginBottom: "0.4rem" }}
                  title="Sei wtrendy: la prima volta che si mappa un file è il reparto tecnico a prepararlo per conto di un'azienda reale. Sceglila qui: serve a generare la Sigla Import giusta (3 lettere di QUESTA azienda, non 'wtrendy') e ad assegnarle il template appena lo salvi."
                >
                  Azienda per cui stai mappando:{" "}
                  <select value={aziendaWtrendySelezionata} onChange={(e) => setAziendaWtrendySelezionata(e.target.value)}>
                    <option value="">-- scegli azienda --</option>
                    {aziendeDisponibili.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </label>
              )}
              <label>Nome cliente (per salvare)</label>
              <div className="riga-controlli">
                <input type="text" value={nomeCliente} onChange={(e) => setNomeCliente(e.target.value)} placeholder="es. Montblanc" />
                <button className="btn-primario" onClick={salvaTemplate}>
                  {templateAttivo?.id ? "Aggiorna template" : "Salva template"}
                </button>
                {templateAttivo?.id && (
                  <button className="btn-link-danger" onClick={() => rimuoviTemplate(templateAttivo.id)}>Elimina</button>
                )}
              </div>
              <p className="testo-secondario" style={{ marginTop: "0.4rem", marginBottom: 0 }} title="Non è più scelta a mano: la genera l'app al salvataggio, nel formato AZIENDA_CLIENTE_progressivo, e resta fissa da lì in poi.">
                Sigla Import: {templateAttivo?.siglaImport
                  ? <strong style={{ fontFamily: "'Roboto Mono', Consolas, monospace" }}>{templateAttivo.siglaImport}</strong>
                  : "verrà generata al salvataggio"}
              </p>
            </div>
          )}
        </div>
        {messaggioTemplate && <p className="testo-secondario">{messaggioTemplate}</p>}

        {puoMappare && (
          <>
            <h3 style={{ marginTop: "1.5rem" }}>Template salvati</h3>
            {templates.length === 0 && <p className="testo-secondario">Nessun template salvato ancora.</p>}
            {templates.length > 0 && (
              <table className="tabella-elenco">
                <thead>
                  <tr><th>Cliente</th><th>Sigla Import</th><th>Aggiornato</th><th></th><th></th><th></th></tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id}>
                      <td>{t.cliente}</td>
                      <td style={{ fontFamily: "'Roboto Mono', Consolas, monospace" }}>{t.siglaImport || "—"}</td>
                      <td>{new Date(t.aggiornato).toLocaleString("it-IT")}</td>
                      <td>
                        <button
                          onClick={() => chiediFilePerRidefinireTemplate(t.id)}
                          title="Carica di nuovo il file di questo cliente: potrai rivedere/correggere foglio e riga delle intestazioni senza perdere la mappatura colonne già salvata"
                        >
                          Modifica
                        </button>
                      </td>
                      <td>
                        <button
                          onClick={() => duplicaTemplate(t.id)}
                          title={`Copia il mapping di "${t.cliente}" in un template nuovo, per un'altra azienda che usa lo stesso formato file — con una sigla import tutta sua`}
                        >
                          Duplica
                        </button>
                      </td>
                      <td><button className="btn-link-danger" onClick={() => rimuoviTemplate(t.id)}>Elimina</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </SezionePasso>

      {puoMappare && (
      <section className="riquadro">
        <h2>3. Mappa i campi</h2>
        <p className="testo-secondario">
          I campi segnati con <span style={{ color: "var(--cf-danger-text)", fontWeight: 700 }}>*</span> sono
          obbligatori per l'API del gestionale: senza, l'importazione viene bloccata prima di scrivere qualunque cosa.
        </p>
        {campiObbligatoriNonCompilati.length > 0 && (
          <p className="errore">
            ⚠️ Campi obbligatori non ancora compilati: {campiObbligatoriNonCompilati.join(", ")}.
          </p>
        )}
        {!suggerimentiNascosti && Object.keys(suggerimentiAttivi).length > 0 && (
          <div
            className="riga-controlli"
            style={{
              marginBottom: "1rem", padding: "0.5rem 0.75rem", borderRadius: 6,
              background: "var(--cf-bg-accent-soft, rgba(34,211,238,0.10))", border: "1px solid var(--cf-border)",
              flexWrap: "wrap",
            }}
          >
            <span>
              💡 <strong>{Object.keys(suggerimentiAttivi).length}</strong> camp{Object.keys(suggerimentiAttivi).length === 1 ? "o ha" : "i hanno"} un
              {" "}suggerimento dallo storico dei template — colonne o valori usati di solito per campi come questi.
            </span>
            <button className="btn-primario" onClick={applicaTuttiSuggerimenti}>Applica tutti</button>
            <button onClick={() => setSuggerimentiNascosti(true)}>Nascondi</button>
          </div>
        )}
        <div className="riga-controlli" style={{ marginBottom: "1rem" }}>
          {campiConfig.gruppi.map((g) => (
            <button
              key={g.chiave}
              className={tabAttiva === g.chiave ? "btn-primario" : ""}
              onClick={() => setTabAttiva(g.chiave)}
            >
              {g.etichetta}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: "1rem", padding: "0.9rem 1rem", border: "1px solid var(--cf-border)", borderRadius: "8px", background: "var(--cf-bg-subtle)" }}>
          <h3 style={{ marginTop: 0 }}>Opzioni avanzate file</h3>
          <p className="testo-secondario" style={{ marginTop: 0 }}>
            Utili per file "ibridi" con dati non organizzati in semplici colonne (es. un Excel con il layout di un PDF).
          </p>
          <label className="riga-controlli">
            <input
              type="checkbox"
              checked={!!mapping.__forwardFill?.attivo}
              onChange={(e) => attivaForwardFill(e.target.checked)}
            />
            Alcune righe hanno celle vuote che vanno ereditate dalla riga sopra (celle unite nel file originale)
          </label>
          {mapping.__forwardFill?.attivo && (
            fileCaricato ? (
              <div className="scelta-mapping" style={{ marginTop: "0.5rem" }}>
                {fileCaricato.headers.map((h, indice) => (
                  <label key={indice} className="riga-controlli" style={{ display: "inline-flex", marginRight: "1rem" }}>
                    <input
                      type="checkbox"
                      checked={(mapping.__forwardFill.colonne || []).includes(h)}
                      onChange={() => alternaColonnaForwardFill(h)}
                    />
                    {h}
                  </label>
                ))}
              </div>
            ) : (
              <p className="errore">Carica un file per scegliere su quali colonne ereditare il valore.</p>
            )
          )}
          <label className="riga-controlli" style={{ marginTop: "0.75rem" }}>
            <input
              type="checkbox"
              checked={!!mapping.__escludiRighe?.attivo}
              onChange={(e) => {
                const attivo = e.target.checked;
                // Attivando per la prima volta, si preimposta subito "Total"
                // anche nello STATO (non solo mostrato nel campo qui sotto):
                // prima il campo mostrava "Total" come valore di comodo ma
                // finché l'utente non ci scriveva sopra a mano lo stato
                // restava vuoto — sembrava impostato ma il filtro backend
                // (filtraRigheEscluse in routes/ordini.js) non faceva nulla,
                // perché controlla "contiene", non "attivo".
                impostaEscludiRighe("attivo", attivo);
                if (attivo && !mapping.__escludiRighe?.contiene) impostaEscludiRighe("contiene", "Total");
              }}
            />
            Escludi le righe che contengono il testo
          </label>
          {mapping.__escludiRighe?.attivo && (
            <input
              type="text"
              value={mapping.__escludiRighe?.contiene ?? ""}
              placeholder='es. "Total"'
              onChange={(e) => impostaEscludiRighe("contiene", e.target.value)}
              style={{ marginTop: "0.4rem", maxWidth: "260px" }}
            />
          )}
          <p className="testo-secondario" style={{ marginTop: "0.4rem" }}>
            Utile per righe di subtotale/totale intercalate tra i dati (es. "Style Total", "Grand Total"): esclusa
            qualunque riga con almeno una cella che contiene questo testo (non sensibile a maiuscole/minuscole).
          </p>
        </div>

        {tabAttiva === "taglie" && (
          <div style={{ marginBottom: "1rem", padding: "0.9rem 1rem", border: "1px solid var(--cf-border)", borderRadius: "8px", background: "var(--cf-bg-subtle)" }}>
            <label className="riga-controlli">
              <input
                type="checkbox"
                checked={!!taglieOriz?.attivo}
                onChange={(e) => attivaTaglieOrizzontali(e.target.checked)}
              />
              Le taglie sono su colonne separate del file (una colonna per misura, es. "35", "36", "37"...)
            </label>
            <p className="testo-secondario">
              Attivalo se nel file cliente ogni misura è una colonna a sé (con la quantità nella cella) invece che una
              riga per misura: per ogni riga del file verrà generata una riga d'ordine per ciascuna colonna scelta qui
              sotto che ha una quantità valida, ripetendo gli altri campi (Testata/Riga) che restano invariati.
            </p>
            {taglieOriz?.attivo && (
              fileCaricato ? (
                <div className="scelta-mapping">
                  {fileCaricato.headers.map((h, indice) => (
                    <label key={indice} className="riga-controlli" style={{ display: "inline-flex", marginRight: "1rem" }}>
                      <input
                        type="checkbox"
                        checked={(taglieOriz.colonne || []).includes(h)}
                        onChange={() => alternaColonnaTaglia(h)}
                      />
                      {h}
                    </label>
                  ))}
                </div>
              ) : (
                <p className="errore">Carica un file per scegliere quali colonne sono le misure.</p>
              )
            )}
          </div>
        )}

        {tabAttiva === "taglie" && (
          <div style={{ marginBottom: "1rem", padding: "0.9rem 1rem", border: "1px solid var(--cf-border)", borderRadius: "8px", background: "var(--cf-bg-subtle)" }}>
            <label className="riga-controlli">
              Conversione nome taglia (mezze taglie, es. "36.5"):
              <select
                value={mapping.__conversioneTaglia || "nessuna"}
                onChange={(e) => impostaConversioneTaglia(e.target.value)}
              >
                {Object.entries(ETICHETTE_CONVERSIONE_TAGLIA).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <p className="testo-secondario">
              Il nome della taglia nel file cliente ("36.5") spesso non è scritto come nel gestionale, e cambia da
              cliente a cliente: applicata qui, appena prima di generare le righe, non serve sistemarla a mano ogni
              volta. Esempio: "36.5" → "<strong>{convertiTaglia("36.5", mapping.__conversioneTaglia || "nessuna")}</strong>".
              Non tocca le taglie intere (es. "37").
            </p>
          </div>
        )}

        {tabAttiva === "taglie" && taglieOriz?.attivo && (
          <div style={{ marginBottom: "1rem", padding: "0.9rem 1rem", border: "1px solid var(--cf-border)", borderRadius: "8px", background: "var(--cf-bg-subtle)" }}>
            <label>Simboli di mezza taglia aggiuntivi</label>
            <p className="testo-secondario">
              Oltre a ".5"/",5" e a una barra da sola ("37/"): alcuni clienti usano un proprio simbolo per la mezza
              taglia (es. "36+"). Registralo una volta sola — da quel momento viene convertito come la barra sola,
              secondo la modalità scelta sopra, e resta salvato in questo template per i prossimi file dello stesso
              cliente.
            </p>
            {simboliPersonalizzatiAttuali.length > 0 && (
              <div className="riga-controlli" style={{ flexWrap: "wrap", marginBottom: "0.5rem" }}>
                {simboliPersonalizzatiAttuali.map((simbolo) => (
                  <span
                    key={simbolo}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "0.35rem",
                      padding: "0.15rem 0.5rem", border: "1px solid var(--cf-border)", borderRadius: "999px",
                      fontFamily: "'Roboto Mono', Consolas, monospace",
                    }}
                  >
                    "{simbolo}" → mezza taglia
                    <button
                      type="button"
                      className="btn-link-danger"
                      onClick={() => rimuoviSimboloTaglia(simbolo)}
                      title={`Smetti di riconoscere "${simbolo}" come mezza taglia`}
                      style={{ padding: 0 }}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
            {simboliTagliaNonRiconosciuti.length > 0 && simboliTagliaNonRiconosciuti.map(({ simbolo, colonne }) => (
              <p key={simbolo} className="errore" style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                ⚠️ Trovata {colonne.length > 1 ? `nel file una colonna come "${colonne[0]}" (e altre ${colonne.length - 1} uguali)` : `nel file la colonna "${colonne[0]}"`}
                {" "}con un simbolo mai registrato prima: "{simbolo}".
                <button type="button" className="btn-primario" onClick={() => registraSimboloTaglia(simbolo, colonne)}>
                  Registra "{simbolo}" come mezza taglia (seleziona anche {colonne.length > 1 ? "le colonne" : "la colonna"})
                </button>
              </p>
            ))}
            {simboliTagliaNonRiconosciuti.length === 0 && simboliPersonalizzatiAttuali.length === 0 && (
              <p className="testo-secondario">
                Nessun simbolo da registrare: le colonne scelte come taglia usano già una notazione riconosciuta.
              </p>
            )}
          </div>
        )}

        <div className="scroll-orizzontale">
          <table className="tabella-mapping">
            <thead>
              <tr><th>Campo</th><th>Tipo</th><th>Valore</th></tr>
            </thead>
            <tbody>
              {gruppoAttivo.campi
                .filter((campo) => !(tabAttiva === "taglie" && taglieOriz?.attivo && (campo === "Taglia_Nome" || campo === "Taglia_Qta")))
                .map((campo) => {
                const regola = mapping[campo] || { tipo: "ignora" };
                const obbligatorio = (campiConfig.campiObbligatori || []).includes(campo);
                // SiglaImport non è più una regola scelta a mano (vedi
                // generaSiglaImport lato server): resta in elenco per
                // trasparenza, ma sola lettura — il valore vero è
                // templateAttivo.siglaImport, iniettato in mapping.SiglaImport
                // dall'effect qui sopra appena il template ce l'ha.
                if (campo === "SiglaImport") {
                  return (
                    <tr key={campo}>
                      <td>
                        {etichettaCampo(campo)}
                        {obbligatorio && (
                          <span style={{ color: "var(--cf-danger-text)", fontWeight: 700 }} title="Campo obbligatorio per l'API del gestionale: non può restare vuoto">
                            {" "}*
                          </span>
                        )}
                      </td>
                      <td className="testo-secondario">generata dall'app</td>
                      <td>
                        {templateAttivo?.siglaImport ? (
                          <strong style={{ fontFamily: "'Roboto Mono', Consolas, monospace" }}>{templateAttivo.siglaImport}</strong>
                        ) : (
                          <span className="testo-secondario" style={{ fontStyle: "italic" }}>verrà generata al salvataggio del template</span>
                        )}
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={campo}>
                    <td>
                      {etichettaCampo(campo)}
                      {obbligatorio && (
                        <span style={{ color: "var(--cf-danger-text)", fontWeight: 700 }} title="Campo obbligatorio per l'API del gestionale: non può restare vuoto">
                          {" "}*
                        </span>
                      )}
                    </td>
                    <td>
                      <select value={regola.tipo} onChange={(e) => cambiaTipo(campo, e.target.value)}>
                        {Object.entries(ETICHETTE_TIPO).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                    <td>
                      {(regola.tipo === "colonna" || regola.tipo === "colonnaEditabile") && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                          {regola.tipo === "colonnaEditabile" && (
                            <span className="testo-secondario">
                              Il valore letto qui è un codice del fornitore: verrà convertito nel tuo codice tramite
                              la tabella di conversione (bottone "🔀 Conversione" nell'intestazione della colonna in
                              anteprima). {!templateAttivo?.id && "Salva il template per iniziare a registrare le conversioni."}
                            </span>
                          )}
                          {fileCaricato ? (
                            <div className="riga-controlli">
                              <select value={regola.colonna || ""} onChange={(e) => impostaColonna(campo, e.target.value)}>
                                <option value="">-- colonna --</option>
                                {fileCaricato.headers.map((h, indice) => <option key={indice} value={h}>{h}</option>)}
                              </select>
                              <button
                                type="button"
                                onClick={() => setModaleColonna({ campo })}
                                title="Scegli la colonna cliccandola nell'anteprima del file, invece di cercarla per nome"
                              >
                                🖱️ Scegli dal file
                              </button>
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={regola.colonna || ""}
                              placeholder="nome colonna file cliente"
                              onChange={(e) => impostaColonna(campo, e.target.value)}
                            />
                          )}
                          {regola.colonna && fileCaricato && (
                            regola.estrazione ? (
                              <div className="riga-controlli">
                                <span className="testo-secondario">
                                  estratto: "{estraiValore(fileCaricato.anteprima[0]?.[regola.colonna], regola.estrazione) || "—"}"
                                </span>
                                <button onClick={() => setModaleEstrazione({ campo, nomeColonnaDestinazione: campo })}>
                                  Modifica estrazione
                                </button>
                                <button onClick={() => setModaleEstrazione({ campo, nomeColonnaDestinazione: campo, daZero: true })}>
                                  🔄 Rifai da capo
                                </button>
                                <button onClick={() => impostaEstrazione(campo, undefined)}>
                                  Usa valore intero
                                </button>
                              </div>
                            ) : (
                              <button onClick={() => setModaleEstrazione({ campo, nomeColonnaDestinazione: campo })}>
                                ✂️ Estrai solo una parte
                              </button>
                            )
                          )}
                        </div>
                      )}
                      {regola.tipo === "fisso" && (
                        <input
                          type="text"
                          value={regola.valore || ""}
                          placeholder='valore fisso, oppure =FORMULA(...)'
                          onChange={(e) => impostaValore(campo, e.target.value)}
                        />
                      )}
                      {regola.tipo === "cella" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                          <div className="riga-controlli">
                            <input
                              type="text"
                              value={regola.cella || ""}
                              placeholder="es. C2"
                              style={{ maxWidth: "90px" }}
                              onChange={(e) => impostaCella(campo, e.target.value.toUpperCase())}
                            />
                            {fileCaricato?.zonaMetadata && (
                              <button onClick={() => setModaleCella({ campo })}>📍 Scegli da file</button>
                            )}
                          </div>
                          <span className="testo-secondario">
                            Valore fisso per tutte le righe, letto dalla cella indicata nel foglio originale.
                          </span>
                        </div>
                      )}
                      {regola.tipo === "manuale" && (
                        <span className="testo-secondario">
                          Nessun valore da impostare qui: la colonna comparirà (vuota) nella tabella di anteprima più
                          sotto, da compilare a mano riga per riga prima di esportare/importare.
                        </span>
                      )}
                      {!suggerimentiNascosti && suggerimentiAttivi[campo] && (
                        <div className="riga-controlli" style={{ marginTop: "0.3rem", fontSize: "0.85em" }}>
                          <span className="testo-secondario">
                            💡 di solito:{" "}
                            {suggerimentiAttivi[campo].tipo === "fisso"
                              ? <>valore fisso «<strong>{suggerimentiAttivi[campo].valore}</strong>»</>
                              : <>dalla colonna «<strong>{suggerimentiAttivi[campo].colonna}</strong>»{!suggerimentiAttivi[campo].esatto && " (nome simile)"}</>}
                            {" "}({suggerimentiAttivi[campo].vistoIn}/{suggerimentiAttivi[campo].suTotale} template)
                          </span>
                          <button onClick={() => applicaSuggerimento(campo)}>usa</button>
                          <button className="btn-link-danger" onClick={() => setSuggerimentiIgnorati((p) => [...p, campo])} title="Non propormelo per questo campo">✕</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      )}

      <SezionePasso
        numero={wizard ? passiWizard.findIndex((p) => p.chiave === "anteprima") + 1 : 4}
        titolo="Anteprima e importazione"
        stato={statoDiPasso("anteprima")}
      >
        {eWtrendy && anteprimaRighe.length > 0 && (
          <p className="riga-controlli" style={{ marginTop: 0 }}>
            <label title="Gli sviluppi (Taglia_NomeSviluppo) sono per azienda: scegli qui per quale stai preparando questo mapping, per vedere e assegnare i SUOI sviluppi veri — non una lista a parte.">
              Sviluppi per azienda:{" "}
              <select value={aziendaWtrendySelezionata} onChange={(e) => setAziendaWtrendySelezionata(e.target.value)}>
                <option value="">-- scegli azienda --</option>
                {aziendeDisponibili.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
          </p>
        )}
        <div className="riga-controlli">
          <button onClick={calcolaAnteprima} disabled={calcolando || !fileCaricato}>
            {calcolando ? "Calcolo..." : "Calcola anteprima"}
          </button>
          <button
            onClick={esportaAnteprima}
            disabled={esportandoAnteprima || anteprimaRighe.length === 0}
            title={anteprimaRighe.length === 0 ? "Calcola prima l'anteprima" : undefined}
          >
            {esportandoAnteprima ? "Esportazione..." : "📥 Esporta anteprima in Excel"}
          </button>
          <button
            className="btn-primario"
            onClick={avviaImportazione}
            disabled={avviando || anteprimaRighe.length === 0 || valoriNomeSviluppoNonValidi.length > 0 || totaleConversioniNonRisolte > 0 || righeConCampoObbligatorioMancante.size > 0 || (verificaTotali && !verificaTotali.ok) || simboliTagliaAnteprima.length > 0}
            title={
              anteprimaRighe.length === 0 ? "Calcola prima l'anteprima"
                : valoriNomeSviluppoNonValidi.length > 0 ? `${valoriNomeSviluppoNonValidi.length} valori di Taglia_NomeSviluppo non sono negli sviluppi validi: usa "🔤 Sviluppi" qui sotto per risolverli.`
                : totaleConversioniNonRisolte > 0 ? `${totaleConversioniNonRisolte} codici del fornitore non hanno ancora una conversione salvata: usa "🔀 Conversione" qui sotto per associarli.`
                : righeConCampoObbligatorioMancante.size > 0 ? `${righeConCampoObbligatorioMancante.size} riga/e hanno un campo obbligatorio mancante o non valido (es. quantità a 0): correggile o eliminale con "🗑️ Elimina" qui sotto.`
                : (verificaTotali && !verificaTotali.ok) ? "Il totale delle quantità in anteprima non corrisponde al file caricato: controlla il riquadro \"Controllo totali\" qui sotto."
                : simboliTagliaAnteprima.length > 0 ? `Simboli di mezza taglia mai registrati: ${simboliTagliaAnteprima.map((s) => `"${s.simbolo}"`).join(", ")}. Registrali qui sotto prima di avviare l'importazione.`
                : "Scrive davvero sulla tabella di frontiera dell'ERP (TB_ORDERSIMPORT), tramite l'API del collega"
            }
          >
            {avviando ? "Importazione..." : "Avvia importazione"}
          </button>
          {aziendaSviluppi && anteprimaRighe.length > 0 && (
            <button
              onClick={() => setMostraModaleAssegna(true)}
              title="Scrive direttamente uno sviluppo su Taglia_NomeSviluppo per più righe insieme — utile quando il file cliente non ha affatto un 'nome calzata' sorgente"
            >
              🔤 Assegna sviluppo alle taglie
            </button>
          )}
          {righeConAnomaliaCodice.size > 0 && (
            <button
              onClick={() => setSoloAnomalieCodice((v) => !v)}
              title="Mostra solo le righe segnalate da 🔢⚠️ nelle intestazioni (codici con un numero di cifre diverso dal resto) — utile su file grandi, invece di scorrerli tutti"
              style={soloAnomalieCodice ? { backgroundColor: "#dc2626", color: "#fff", borderColor: "#dc2626" } : undefined}
            >
              🔢⚠️ {soloAnomalieCodice ? "Mostra tutte le righe" : `Solo righe da correggere (${righeConAnomaliaCodice.size})`}
            </button>
          )}
          {colonneCodiceNonUniforme.size > 0 && (
            <button
              onClick={() => setMostraCorreggiCodici(true)}
              title="Correggi in blocco tutti i codici segnalati — per valore, non riga per riga: una correzione si applica a tutte le righe che hanno quel valore in un colpo solo"
            >
              ✏️ Correggi codici
            </button>
          )}
          {righeConCampoObbligatorioMancante.size > 0 && (
            <button
              onClick={eliminaRigheConCampoObbligatorioMancante}
              title="Elimina tutte le righe con un campo obbligatorio mancante o non valido (es. quantità a 0) — chiede conferma, non si può annullare"
              style={{ backgroundColor: "#dc2626", color: "#fff", borderColor: "#dc2626" }}
            >
              🗑️ Elimina righe senza campi obbligatori ({righeConCampoObbligatorioMancante.size})
            </button>
          )}
        </div>
        {simboliTagliaAnteprima.length > 0 && (
          <div className="errore" style={{ marginTop: "0.75rem", padding: "0.8rem 1rem", border: "1px solid var(--cf-danger-text)", borderRadius: "8px" }}>
            <strong>⚠️ Simboli di mezza taglia mai registrati, trovati nei dati di questo file:</strong>
            {simboliTagliaAnteprima.map(({ simbolo, esempi, totaleValori }) => (
              <p key={simbolo} style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap", margin: "0.5rem 0 0" }}>
                "{simbolo}" — es. {esempi.map((e) => `"${e}"`).join(", ")}
                {totaleValori > esempi.length ? ` (+${totaleValori - esempi.length} altre taglie)` : ""}: resta scritta
                così com'è nel file, senza conversione, finché non la registri.
                <button
                  type="button"
                  className="btn-primario"
                  onClick={() => {
                    registraSimboloTaglia(simbolo);
                    setSimboliTagliaAnteprima((prev) => prev.filter((s) => s.simbolo !== simbolo));
                  }}
                >
                  Registra "{simbolo}" come mezza taglia
                </button>
              </p>
            ))}
            <p className="testo-secondario" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
              Dopo aver registrato, scegli (se non l'hai già fatto) la modalità di conversione nella tab "Taglie" e
              ricalcola l'anteprima per vedere i valori convertiti.
            </p>
          </div>
        )}
        {mostraCorreggiCodici && (
          <CorreggiCodiciModal
            colonneAnomale={colonneCodiceNonUniforme}
            righe={anteprimaRighe}
            onApplica={applicaCorrezioniCodice}
            onChiudi={() => setMostraCorreggiCodici(false)}
          />
        )}
        {messaggioImportazione && <p className="testo-secondario" style={{ marginTop: "0.5rem" }}>{messaggioImportazione}</p>}
        {errore && <AvvisoModal messaggio={errore} onChiudi={() => setErrore("")} />}

        {anteprimaRighe.length > 0 && (
          <>
            {eWtrendy && (
              <p className="riga-controlli" style={{ marginTop: "1rem" }}>
                <button type="button" onClick={() => setMostraFunzioni(true)}>📖 Funzioni</button>
              </p>
            )}
            {mostraFunzioni && (
              <LegendaFunzioniModal funzioni={FUNZIONI_ANTEPRIMA_ORDINI} onChiudi={() => setMostraFunzioni(false)} />
            )}
            {filtroAnteprimaAttivo && (
              <p className="testo-secondario">
                Mostrando {righeAnteprimaFiltrate.length} di {anteprimaRighe.length} righe (filtro attivo) —{" "}
                <button className="btn-link" onClick={() => { setFiltriColonnaAnteprima({}); setSoloAnomalieCodice(false); }}>azzera filtri</button>
              </p>
            )}
            <div className="scroll-orizzontale scroll-verticale" ref={contenitoreAnteprimaRef}>
              <table className="tabella-anteprima">
                <thead>
                  <tr>
                    <th style={{ width: "2.2rem" }} title="Elimina riga"></th>
                    {colonneAnteprima.map((c) => (
                      <th key={c}>
                        <div style={{ whiteSpace: "nowrap" }}>
                          {etichettaCampo(c)}{" "}
                          {colonneCasoNonUniforme.has(c) && (
                            <span
                              title={`Questa colonna ha valori sia in MAIUSCOLO che in minuscolo (o misti): usa "AA"/"aa" qui accanto per uniformarli`}
                              style={{ color: "#f59e0b" }}
                            >
                              ⚠️
                            </span>
                          )}{" "}
                          {colonneCodiceNonUniforme.has(c) && (
                            <span
                              title={`Alcuni valori di questa colonna hanno un numero di cifre diverso dal resto (${colonneCodiceNonUniforme.get(c).cifreAttese} cifre nella maggior parte delle righe, es. uno zero finale perso da Excel) e/o del testo in più dopo il codice (es. "99.718163 (cotton)"). Le righe evidenziate in rosso qui sotto sono quelle sospette — controllale prima di esportare, o usa "✏️ Correggi codici".`}
                              style={{ color: "#dc2626" }}
                            >
                              🔢⚠️
                            </span>
                          )}{" "}
                          <button
                            type="button"
                            className="btn-link"
                            title={`Riempie le celle vuote di "${c}" copiando in basso l'ultimo valore trovato sopra, fino alla prossima cella già compilata — non sovrascrive dati esistenti`}
                            onClick={() => riempiColonnaAnteprima(c)}
                          >
                            ↓ riempi
                          </button>{" "}
                          {(indiciMancantiPerCampo.get(c)?.length > 0) && (
                            <>
                              <button
                                type="button"
                                className="btn-link"
                                title={`Vedi e compila le ${indiciMancantiPerCampo.get(c).length} righe dove "${c}" (obbligatorio) è ancora vuoto — senza scorrere tutta la tabella`}
                                onClick={() => setModaleCampoMancante(c)}
                                style={{ color: "var(--cf-danger-text)", fontWeight: 700 }}
                              >
                                ⚠️ {indiciMancantiPerCampo.get(c).length} mancanti
                              </button>{" "}
                            </>
                          )}
                          <button
                            type="button"
                            className="btn-link"
                            title={`Metti in MAIUSCOLO tutti i valori della colonna "${c}"`}
                            onClick={() => trasformaColonnaAnteprima(c, "maiuscolo")}
                          >
                            AA
                          </button>{" "}
                          <button
                            type="button"
                            className="btn-link"
                            title={`Metti in minuscolo tutti i valori della colonna "${c}"`}
                            onClick={() => trasformaColonnaAnteprima(c, "minuscolo")}
                          >
                            aa
                          </button>
                          {c === "Taglia_NomeCalzata" && aziendaSviluppi && (
                            <>
                              {" "}
                              <button
                                type="button"
                                className="btn-link"
                                title="Abbina i valori di questa colonna a uno sviluppo già caricato per la tua azienda"
                                onClick={apriGestioneSviluppi}
                              >
                                🔤 Sviluppi{valoriNomeSviluppoNonValidi.length > 0 ? ` (${valoriNomeSviluppoNonValidi.length})` : ""}
                              </button>
                            </>
                          )}
                          {campiConversioneCodici.includes(c) && templateAttivo?.id && (
                            <>
                              {" "}
                              <button
                                type="button"
                                className="btn-link"
                                title="Converti i codici del fornitore trovati in questa colonna nei tuoi codici"
                                onClick={() => apriConversioneCodici(c)}
                              >
                                🔀 Conversione{(valoriConversioneNonRisolti[c]?.length || 0) > 0 ? ` (${valoriConversioneNonRisolti[c].length})` : ""}
                              </button>
                            </>
                          )}
                        </div>
                        <input
                          type="text"
                          value={filtriColonnaAnteprima[c] || ""}
                          onChange={(e) => setFiltriColonnaAnteprima((prev) => ({ ...prev, [c]: e.target.value }))}
                          placeholder="🔍 filtra..."
                          title={`Mostra solo le righe dove "${c}" contiene questo testo`}
                          style={{ width: "100%", boxSizing: "border-box", marginTop: "0.3rem", fontWeight: 400, textTransform: "none" }}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {finestraAnteprima.altezzaSopra > 0 && (
                    <tr aria-hidden="true" style={{ height: finestraAnteprima.altezzaSopra }}>
                      <td colSpan={colonneAnteprima.length + 1} style={{ padding: 0, border: "none" }} />
                    </tr>
                  )}
                  {righeAnteprimaFiltrate.slice(finestraAnteprima.indicePrimo, finestraAnteprima.indiceUltimo).map(({ riga: r, i }, indiceFinestra) => {
                    const campiMancanti = righeConCampoObbligatorioMancante.get(i);
                    const inAttesaEliminazione = rigaInEliminazione === i;
                    return (
                    <tr
                      key={r.__chiaveRiga}
                      ref={indiceFinestra === 0 ? finestraAnteprima.refPrimaRiga : undefined}
                      style={inAttesaEliminazione ? { backgroundColor: "var(--cf-danger-bg)" } : undefined}
                    >
                      <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        {inAttesaEliminazione ? (
                          <span className="riga-controlli" style={{ gap: "0.3rem", justifyContent: "center" }}>
                            <button
                              type="button"
                              className="btn-link-danger"
                              title="Conferma: elimina questa riga"
                              onClick={() => confermaEliminaRigaAnteprima(i)}
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              className="btn-link"
                              title="Annulla, non eliminare"
                              onClick={annullaEliminaRigaAnteprima}
                            >
                              ✕
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn-link-danger"
                            title="Elimina questa riga"
                            onClick={() => chiediEliminaRigaAnteprima(i)}
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                      {colonneAnteprima.map((c, indiceColonna) => {
                        const infoCodice = colonneCodiceNonUniforme.get(c);
                        const infoRigaCodice = infoCodice?.righe.get(i);
                        const anomaliaCodice = infoRigaCodice !== undefined;
                        const campoMancante = campiMancanti?.includes(c);
                        return (
                          <td key={c}>
                            <input
                              type="text"
                              ref={refCellaAnteprima(i, c)}
                              defaultValue={r[c] ?? ""}
                              list={c === "Taglia_NomeCalzata" ? "lista-sviluppi-ordini" : undefined}
                              title={
                                campoMancante ? "Campo obbligatorio per l'API del gestionale: vuoto o non valido (es. quantità a 0) — questa riga non può essere importata così com'è."
                                  : anomaliaCodice ? [
                                      infoRigaCodice.cifreTrovate !== infoCodice.cifreAttese && `${infoRigaCodice.cifreTrovate} cifre invece di ${infoCodice.cifreAttese} — controlla che non manchi una cifra (es. uno zero finale perso da Excel).`,
                                      infoRigaCodice.testoExtra && `Testo in più dopo il codice: "${infoRigaCodice.testoExtra}" — non fa parte del codice articolo.`,
                                    ].filter(Boolean).join(" ")
                                  : undefined
                              }
                              style={{
                                ...(larghezzeColonneAnteprima ? { width: `${larghezzeColonneAnteprima[c]}ch` } : {}),
                                ...(anomaliaCodice ? { border: "1px solid #dc2626", backgroundColor: "#fee2e2" } : {}),
                                ...(campoMancante ? { border: "1px solid #d97706", backgroundColor: "#fef3c7" } : {}),
                              }}
                              onPaste={(e) => onIncollaCellaAnteprima(e, i, indiceColonna)}
                              onBlur={(e) => {
                                if (e.target.value !== String(r[c] ?? "")) modificaCellaAnteprima(i, c, e.target.value);
                              }}
                            />
                          </td>
                        );
                      })}
                    </tr>
                    );
                  })}
                  {finestraAnteprima.altezzaSotto > 0 && (
                    <tr aria-hidden="true" style={{ height: finestraAnteprima.altezzaSotto }}>
                      <td colSpan={colonneAnteprima.length + 1} style={{ padding: 0, border: "none" }} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {colonneAnteprima.includes("Taglia_NomeCalzata") && (
              <datalist id="lista-sviluppi-ordini">
                {legendaSviluppi.sviluppi.map((s) => <option key={s} value={s} />)}
              </datalist>
            )}
            {valoriNomeSviluppoNonValidi.length > 0 && (
              <p className="errore">
                ⚠️ {valoriNomeSviluppoNonValidi.length} valori di Taglia_NomeSviluppo non sono ancora negli sviluppi
                validi per questo cliente — usa "🔤 Sviluppi" nell'intestazione della colonna per risolverli (o
                scegline uno direttamente in ogni cella: l'elenco suggerisce quelli già noti).
              </p>
            )}
            {totaleConversioniNonRisolte > 0 && (
              <p className="errore">
                ⚠️ {totaleConversioniNonRisolte} codici del fornitore non hanno ancora una conversione salvata — usa
                "🔀 Conversione" nell'intestazione della colonna per associarli al tuo codice.
              </p>
            )}
            {verificaTotali && (
              // Confronto "il file dichiara X, l'anteprima ne genera Y" — vedi
              // calcolaVerificaTotali in routes/ordini.js per il "perché" (una
              // colonna taglia dimenticata in mappatura non fa sparire la riga
              // dall'anteprima, solo la sua quantità, quindi da soli i dati in
              // tabella non bastano ad accorgersene). Blocca "Avvia
              // importazione" finché non torna — vedi il disabled del bottone.
              <div
                style={{
                  marginTop: "1rem", padding: "0.9rem 1rem", borderRadius: "8px",
                  border: `1px solid ${verificaTotali.ok ? "var(--cf-border)" : "var(--cf-danger-text)"}`,
                  background: verificaTotali.ok ? "var(--cf-bg-subtle)" : "var(--cf-danger-bg)",
                }}
              >
                <strong>
                  {verificaTotali.ok
                    ? "✅ Controllo totali: la quantità in anteprima corrisponde al file"
                    : "❌ Controllo totali: qualcosa non torna rispetto al file"}
                  {" "}
                  <span className="testo-secondario" style={{ fontWeight: 400 }}>
                    ({verificaTotali.modalita === "orizzontale" ? "taglie su colonne separate" : "una riga per taglia"})
                  </span>
                </strong>
                <p className="testo-secondario" style={{ marginTop: "0.4rem", marginBottom: verificaTotali.ok ? 0 : "0.6rem" }}>
                  Quantità totale nel file: <strong>{verificaTotali.sommaFileTotale}</strong> — quantità totale in
                  anteprima: <strong>{verificaTotali.sommaAnteprimaTotale}</strong>.
                </p>
                {verificaTotali.colonneNonSelezionate.length > 0 && (
                  <p className="errore" style={{ marginTop: 0, marginBottom: "0.6rem" }}>
                    ⚠️ Nel file ci sono colonne che sembrano taglie (solo cifre, es. "43") ma non sono selezionate in
                    "Le taglie sono su colonne separate" (Taglie → OPZIONI AVANZATE FILE):{" "}
                    <strong>{verificaTotali.colonneNonSelezionate.join(", ")}</strong>. Selezionale, altrimenti la
                    loro quantità non verrà importata.
                  </p>
                )}
                {verificaTotali.totaleRigheConMismatch > 0 && (
                  <>
                    <p className="errore" style={{ marginTop: 0, marginBottom: "0.3rem" }}>
                      ⚠️ {verificaTotali.totaleRigheConMismatch} riga/e del file hanno una somma diversa da quella
                      generata in anteprima:
                    </p>
                    <ul style={{ marginTop: 0, marginBottom: 0, fontSize: "0.85rem" }}>
                      {verificaTotali.righeConMismatch.slice(0, 10).map((r) => (
                        <li key={r.riga}>
                          Riga {r.riga}{r.riferimento ? ` (${r.riferimento})` : ""}: file = {r.sommaFile}, anteprima = {r.sommaAnteprima}
                        </li>
                      ))}
                    </ul>
                    {verificaTotali.totaleRigheConMismatch > verificaTotali.righeConMismatch.length && (
                      <p className="testo-secondario" style={{ marginTop: "0.3rem", marginBottom: 0 }}>
                        ...e altre {verificaTotali.totaleRigheConMismatch - verificaTotali.righeConMismatch.length} riga/e.
                      </p>
                    )}
                  </>
                )}
                {/* Report completo, sempre visibile (anche a "tutto ok") — non
                    solo un confronto sui totali aggregati: due errori di segno
                    opposto su taglie o combinazioni diverse si annullerebbero
                    nel totale generale e resterebbero invisibili senza questo
                    dettaglio riga per riga. */}
                {verificaTotali.righePerTaglia.length > 0 && (
                  <details open style={{ marginTop: "0.7rem" }}>
                    <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                      Per taglia ({verificaTotali.righePerTaglia.length})
                    </summary>
                    <div style={{ maxHeight: "220px", overflowY: "auto", marginTop: "0.4rem" }}>
                      <table style={{ width: "100%", fontSize: "0.85rem", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ textAlign: "left" }}>
                            <th>Taglia</th><th>File</th><th>Anteprima</th>
                          </tr>
                        </thead>
                        <tbody>
                          {verificaTotali.righePerTaglia.map((r) => {
                            const diversi = Math.round(r.sommaFile * 1000) !== Math.round(r.sommaAnteprima * 1000);
                            return (
                              <tr key={r.taglia} style={diversi ? { color: "var(--cf-danger-text)", fontWeight: 600 } : undefined}>
                                <td>{r.taglia}</td><td>{r.sommaFile}</td><td>{r.sommaAnteprima}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
                {verificaTotali.righePerCombinazione.length > 0 && (
                  <details open style={{ marginTop: "0.7rem" }}>
                    <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                      Per articolo/variante/combinazione ({verificaTotali.righePerCombinazione.length})
                    </summary>
                    <div style={{ maxHeight: "260px", overflowY: "auto", marginTop: "0.4rem" }}>
                      <table style={{ width: "100%", fontSize: "0.85rem", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ textAlign: "left" }}>
                            <th>Combinazione</th><th>File</th><th>Anteprima</th>
                          </tr>
                        </thead>
                        <tbody>
                          {verificaTotali.righePerCombinazione.map((r) => {
                            const diversi = Math.round(r.sommaFile * 1000) !== Math.round(r.sommaAnteprima * 1000);
                            return (
                              <tr key={r.etichetta} style={diversi ? { color: "var(--cf-danger-text)", fontWeight: 600 } : undefined}>
                                <td>{r.etichetta}</td><td>{r.sommaFile}</td><td>{r.sommaAnteprima}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            )}
          </>
        )}
      </SezionePasso>

      {mostraModaleSviluppi && (
        <LegendaSviluppiModal
          azienda={aziendaSviluppi}
          sviluppi={legendaSviluppi.sviluppi}
          valoriDaRisolvere={valoriNomeSviluppoNonValidi}
          puoCreareSviluppi={eWtrendy}
          onSalvaMappature={salvaMappatureCliente}
          onCreaSviluppo={creaSviluppoCliente}
          onEliminaSviluppo={rimuoviSviluppoCliente}
          onChiudi={() => setMostraModaleSviluppi(false)}
        />
      )}

      {mostraModaleAssegna && (
        <AssegnaSviluppoModal
          sviluppi={legendaSviluppi.sviluppi}
          gruppi={gruppiPerArticolo}
          totaleRighe={anteprimaRighe.length}
          puoCreareSviluppi={eWtrendy}
          onApplicaTutte={applicaSviluppoATutteLeTaglie}
          onApplicaPerGruppo={applicaSviluppoPerArticolo}
          onCreaSviluppo={creaSviluppoCliente}
          onChiudi={() => setMostraModaleAssegna(false)}
        />
      )}

      {modaleConversione && (
        <ConversioneCodiciModal
          campo={modaleConversione.campo}
          valoriDaRisolvere={valoriConversioneNonRisolti[modaleConversione.campo] || []}
          onSalvaMappature={(mappature) => salvaMappatureConversioneCampo(modaleConversione.campo, mappature)}
          onChiudi={() => setModaleConversione(null)}
        />
      )}

      {modaleCampoMancante && (
        <CampiMancantiModal
          campo={modaleCampoMancante}
          indiciMancanti={indiciMancantiPerCampo.get(modaleCampoMancante) || []}
          righe={anteprimaRighe}
          raggruppaPerArticolo={colonneAnteprima.includes("Riga_Articolo") && modaleCampoMancante !== "Riga_Articolo"}
          onScrivi={(i, valore) => scriviCellaAnteprima(i, modaleCampoMancante, valore)}
          onChiudi={() => setModaleCampoMancante(null)}
        />
      )}

      <section className="riquadro">
        <h2>Modelli PDF salvati</h2>
        {puoMappare && (
          <p className="testo-secondario">
            Solo per Import Ordini (separati da quelli di Distinta Base e DDT). Per modificare un modello serve un PDF
            di riferimento (con lo stesso layout) da guardare durante la calibrazione — te lo chiediamo al momento.
          </p>
        )}
        {modelliPdfSalvati.length === 0 && <p className="testo-secondario">Nessun modello PDF salvato ancora.</p>}
        {modelliPdfSalvati.length > 0 && (
          <table className="tabella-elenco">
            <thead>
              <tr><th>Cliente</th><th>Aggiornato</th>{puoMappare && (<><th></th><th></th></>)}</tr>
            </thead>
            <tbody>
              {modelliPdfSalvati.map((m) => (
                <tr key={m.id}>
                  <td>{m.cliente}</td>
                  <td>{new Date(m.aggiornato).toLocaleString("it-IT")}</td>
                  {puoMappare && (
                    <>
                      <td><button onClick={() => chiediFilePerModificaModello(m.id)} disabled={caricamento}>Modifica</button></td>
                      <td><button className="btn-link-danger" onClick={() => eliminaModelloPdfSalvato(m.id)}>Elimina</button></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {modaleEstrazione && fileCaricato && (
        <EstrazioneModal
          nomeColonnaSorgente={mapping[modaleEstrazione.campo]?.colonna}
          nomeColonnaDestinazione={modaleEstrazione.nomeColonnaDestinazione}
          esempi={fileCaricato.anteprima.map((r) => String(r[mapping[modaleEstrazione.campo]?.colonna] ?? ""))}
          estrazioneIniziale={modaleEstrazione.daZero ? null : (mapping[modaleEstrazione.campo]?.estrazione || null)}
          onConferma={(estrazione) => {
            impostaEstrazione(modaleEstrazione.campo, estrazione);
            setModaleEstrazione(null);
          }}
          onAnnulla={() => setModaleEstrazione(null)}
        />
      )}

      {modaleCella && fileCaricato?.zonaMetadata && (
        <SceltaCellaModal
          nomeFile={fileCaricato.nomeOriginale}
          zonaMetadata={fileCaricato.zonaMetadata}
          onScegli={(cella) => { impostaCella(modaleCella.campo, cella); setModaleCella(null); }}
          onAnnulla={() => setModaleCella(null)}
        />
      )}
      {modaleColonna && fileCaricato && (
        <SceltaColonnaModal
          nomeFile={fileCaricato.nomeOriginale}
          etichettaCampo={etichettaCampo(modaleColonna.campo)}
          headers={fileCaricato.headers}
          anteprima={fileCaricato.anteprima}
          colonnaAttuale={mapping[modaleColonna.campo]?.colonna}
          onScegli={(colonna) => { impostaColonna(modaleColonna.campo, colonna); setModaleColonna(null); }}
          onAnnulla={() => setModaleColonna(null)}
        />
      )}
    </div>
  );
}
